export interface PricingTier {
  persons: string;
  price: number;
}

export interface Tour {
  id: string;
  departure_city: 'hurghada' | 'marsa-alam';
  title_de: string;
  title_en: string;
  description_de: string;
  description_en: string;
  price: number;
  duration: string;
  duration_type: 'half_day' | 'full_day' | 'multi_day';
  image_url: string;
  gallery: string[];
  rating: number;
  reviews_count: number;
  highlights_de: string[];
  highlights_en: string[];
  itinerary_de: string[];
  itinerary_en: string[];
  included_de: string[];
  included_en: string[];
  excluded_de: string[];
  excluded_en: string[];
  meeting_point_de: string;
  meeting_point_en: string;
  languages_de: string;
  languages_en: string;
  faq: { question_de: string; question_en: string; answer_de: string; answer_en: string }[];
  pricing_model: 'tiered' | 'flat';
  pricing_tiers?: PricingTier[];
  child_price?: number;
}

export const tours: Tour[] = [
  // ==================== HURGHADA TOURS ====================
  {
    id: 'hurghada-luxor-1day',
    departure_city: 'hurghada',
    title_de: 'Tagesausflug nach Luxor',
    title_en: 'Luxor Day Tour',
    description_de: `Erleben Sie die faszinierende Welt der Pharaonen auf einem privaten Tagesausflug von Hurghada nach Luxor. Gemeinsam mit Ihrem deutschsprachigen Reiseleiter entdecken Sie die bedeutendsten Sehenswürdigkeiten des alten Ägyptens in entspannter Atmosphäre und ohne Zeitdruck.

Besuchen Sie den beeindruckenden Karnak-Tempel, erkunden Sie das berühmte Tal der Könige mit seinen königlichen Gräbern und bestaunen Sie den einzigartigen Tempel der Königin Hatschepsut. Ein weiterer Höhepunkt sind die gewaltigen Memnon-Kolosse, die seit über 3.000 Jahren am Westufer des Nils stehen.

Während der gesamten Tour reisen Sie bequem in einem modernen, klimatisierten Privatfahrzeug. Ein Mittagessen in einem ausgewählten lokalen Restaurant sowie die Möglichkeit zu einer Nilfahrt machen diesen Ausflug zu einem unvergesslichen Erlebnis.

Diese private Luxor-Tour eignet sich perfekt für Reisende, die Ägyptens Geschichte intensiv erleben und dabei Wert auf persönlichen Service, Flexibilität und deutschsprachige Betreuung legen.`,
    description_en: 'Visit the Valley of the Kings, Karnak Temple & Nile crossing – pure history! Experience the fascinating world of the pharaohs on a private day trip to Luxor with English-speaking Egyptologist.',
    price: 100,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/ec296fd396cf2a558ffc16d4e1289a08.jpeg',
    gallery: [
      '/images/hurghada/hurghada-luxor-1day/1.jpg',
      '/images/hurghada/hurghada-luxor-1day/2.jpg',
      '/images/hurghada/hurghada-luxor-1day/3.jpg',
      '/images/hurghada/hurghada-luxor-1day/4.jpg',
      '/images/hurghada/hurghada-luxor-1day/5.jpg',
      '/images/hurghada/hurghada-luxor-1day/6.jpg',
      '/images/hurghada/hurghada-luxor-1day/7.jpg',
      '/images/hurghada/hurghada-luxor-1day/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 312,
    highlights_de: ['Karnak-Tempel', 'Tal der Könige', 'Hatschepsut-Tempel', 'Memnon-Kolosse', 'Deutschsprachiger Ägyptologe', 'Privater Transfer ab Hurghada', 'Mittagessen inklusive', 'Optionale Nilfahrt'],
    highlights_en: ['Valley of the Kings', 'Karnak Temple', 'Colossi of Memnon', 'Hatshepsut Temple', 'Nile Crossing', 'German-speaking Egyptologist', 'Private Transport'],
    itinerary_de: [
      '05:00 – Abholung von Ihrem Hotel in Hurghada mit einem privaten, klimatisierten Fahrzeug.',
      '09:00 – Ankunft in Luxor und Besichtigung des beeindruckenden Karnak-Tempels, der größten Tempelanlage Ägyptens.',
      '11:00 – Möglichkeit zu einer optionalen Nilfahrt über den Nil mit einem Motorboot.',
      '12:00 – Mittagessen in einem ausgewählten lokalen Restaurant in Luxor.',
      '13:30 – Fotostopp an den beeindruckenden Memnon-Kolossen.',
      '14:00 – Besuch des berühmten Tempels der Königin Hatschepsut am Westufer des Nils.',
      '15:00 – Besuch des weltberühmten Tals der Könige mit seinen prachtvollen Königsgräbern.',
      '16:30 – Beginn der Rückfahrt nach Hurghada.',
      '20:00 – Ankunft im Hotel und Ende eines unvergesslichen Tages in Luxor.',
    ],
    itinerary_en: [
      '05:00 — Pickup from your hotel in Hurghada',
      '09:00 — Arrival in Luxor, visit Karnak Temple',
      '11:30 — Visit the Valley of the Kings',
      '13:00 — Lunch at a local restaurant',
      '14:30 — Visit Hatshepsut Temple and Colossi of Memnon',
      '16:00 — Return to Hurghada',
      '20:00 — Arrival at hotel',
    ],
    included_de: ['Privater Transfer im klimatisierten Fahrzeug', 'Deutschsprachiger Reiseleiter', 'Mittagessen', 'Eintrittsgelder laut Programm', 'Überfahrt mit dem Motorboot über den Nil zum Westufer', 'Mineralwasser und Softdrinks während der Fahrt'],
    included_en: ['Transfer', 'Guide', 'Lunch', 'Entrance fees'],
    excluded_de: ['Getränke im Restaurant während des Mittagessens', 'Optionale einstündige Nilfahrt', 'Besuch der Bananeninsel (optional)', 'Persönliche Ausgaben', 'Trinkgeld'],
    excluded_en: ['Drinks at lunch', 'Personal expenses', 'Tips'],
    meeting_point_de: 'Ihr Hotel in Hurghada, Makadi Bay, Sahl Hasheesh oder El Gouna',
    meeting_point_en: 'Your hotel in Hurghada, Makadi Bay, Sahl Hasheesh or El Gouna',
    languages_de: 'Deutsch, Englisch, Arabisch',
    languages_en: 'German, English, Arabic',
    faq: [
      { question_de: 'Wie lange dauert die Fahrt von Hurghada nach Luxor?', question_en: 'How long is the drive from Hurghada to Luxor?', answer_de: 'Die Fahrt von Hurghada nach Luxor dauert je nach Hotelstandort und Verkehrslage etwa 4 bis 4,5 Stunden pro Strecke. Während der Fahrt sind Pausen möglich.', answer_en: 'The drive from Hurghada to Luxor takes about 4 to 4.5 hours each way, depending on your hotel location and traffic. Breaks are possible during the journey.' },
      { question_de: 'Ist die Tour für Kinder geeignet?', question_en: 'Is the tour suitable for children?', answer_de: 'Ja, der private Tagesausflug nach Luxor eignet sich auch für Familien mit Kindern. Durch die private Durchführung kann das Tempo individuell angepasst werden.', answer_en: 'Yes, the private day trip to Luxor is also suitable for families with children. The private nature of the tour allows the pace to be adjusted individually.' },
      { question_de: 'Ist eine Nilfahrt im Preis enthalten?', question_en: 'Is the Nile boat ride included in the price?', answer_de: 'Die Überfahrt mit dem Motorboot über den Nil zum Westufer ist bereits im Tourpreis enthalten. Eine längere Nilfahrt von etwa einer Stunde oder ein Besuch der Bananeninsel können optional vor Ort gegen Aufpreis gebucht werden.', answer_en: 'The motorboat crossing over the Nile to the West Bank is already included in the tour price. A longer Nile boat ride of about one hour or a visit to Banana Island can be booked optionally on site for an additional fee.' },
      { question_de: 'Was sollte ich zur Tour mitbringen?', question_en: 'What should I bring on the tour?', answer_de: 'Wir empfehlen bequeme Kleidung, Sonnenbrille, Sonnencreme, eine Kopfbedeckung sowie etwas Bargeld für persönliche Ausgaben und Trinkgelder.', answer_en: 'We recommend comfortable clothing, sunglasses, sunscreen, a hat, and some cash for personal expenses and tips.' },
      { question_de: 'Wird die Tour auf Deutsch durchgeführt?', question_en: 'Is the tour conducted in German?', answer_de: 'Ja, die Tour wird von einem deutschsprachigen Reiseleiter begleitet, der Ihnen die Geschichte und Sehenswürdigkeiten Luxors ausführlich erklärt.', answer_en: 'Yes, the tour is accompanied by a German-speaking guide who will explain the history and sights of Luxor in detail.' },
      { question_de: 'Ist die Abholung von meinem Hotel inklusive?', question_en: 'Is hotel pickup included?', answer_de: 'Ja, die Abholung und Rückfahrt von Hotels in Hurghada sind im Preis der Tour enthalten.', answer_en: 'Yes, pickup and return from hotels in Hurghada are included in the tour price.' },
    ],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 150 },
      { persons: '3 Pers.', price: 145 },
      { persons: '4 Pers.', price: 140 },
      { persons: '5–6 Pers.', price: 120 },
      { persons: '7–8 Pers.', price: 100 },
    ],
    child_price: 55,
  },
  {
    id: 'hurghada-luxor-2day',
    departure_city: 'hurghada',
    title_de: '2-Tagesausflug nach Luxor',
    title_en: 'Luxor 2-Day Tour',
    description_de: `Erleben Sie die Höhepunkte Luxors auf einer exklusiven privaten 2-Tages-Tour ab Hurghada mit Übernachtung in Luxor. Gemeinsam mit Ihrem deutschsprachigen Reiseleiter entdecken Sie die faszinierende Welt der Pharaonen ohne Zeitdruck und fernab vom Massentourismus.

Am ersten Tag besuchen Sie den beeindruckenden Karnak-Tempel, die größte Tempelanlage Ägyptens. Anschließend erfolgt der Check-in in Ihr Hotel in Luxor. Am Abend haben Sie die Möglichkeit, optional eine entspannte Nilfahrt bei Sonnenuntergang zu unternehmen, bevor Sie den stimmungsvoll beleuchteten Luxor-Tempel besuchen. Danach genießen Sie Ihre Übernachtung in Luxor.

Nach dem Frühstück im Hotel beginnt der zweite Tag mit der Besichtigung der gewaltigen Memnonkolosse. Anschließend besuchen Sie den berühmten Totentempel der Königin Hatschepsut sowie den eindrucksvollen Medinet-Habu-Tempel, eine der beeindruckendsten Tempelanlagen am Westufer des Nils. Danach erwartet Sie das weltberühmte Tal der Könige mit seinen faszinierenden Königsgräbern. Nach einem erlebnisreichen Tag treten Sie die Rückfahrt nach Hurghada an.

Diese private Luxor-Tour ist ideal für Reisende, die Ägyptens Geschichte intensiv erleben möchten und Wert auf Komfort, Flexibilität, deutschsprachige Betreuung und persönliche Reiseerlebnisse legen.`,
    description_en: 'More time for Luxor with overnight stay & detailed sightseeing. Two intensive days in the ancient capital of Egypt with English-speaking Egyptologist.',
    price: 245,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/4c0699d4981fd2b2f1fca1720010d1e6.jpeg',
    gallery: [
      '/images/hurghada/hurghada-luxor-2day/1.jpg',
      '/images/hurghada/hurghada-luxor-2day/2.jpg',
      '/images/hurghada/hurghada-luxor-2day/3.jpg',
      '/images/hurghada/hurghada-luxor-2day/4.jpg',
      '/images/hurghada/hurghada-luxor-2day/5.jpg',
      '/images/hurghada/hurghada-luxor-2day/6.jpg',
      '/images/hurghada/hurghada-luxor-2day/7.jpg',
      '/images/hurghada/hurghada-luxor-2day/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 198,
    highlights_de: ['Karnak-Tempel', 'Luxor-Tempel bei Nacht', 'Tal der Könige', 'Hatschepsut-Tempel', 'Memnonkolosse', 'Medinet-Habu-Tempel', 'Übernachtung in Luxor', 'Deutschsprachiger Ägyptologe'],
    highlights_en: ['Valley of the Kings', 'Karnak Temple', 'Luxor Temple at night', 'Nile Felucca ride', 'Hotel overnight', 'Private Egyptologist'],
    itinerary_de: [
      'Tag 1: Abholung vom Hotel in Hurghada',
      'Besuch des Karnak-Tempels',
      'Mittagessen in einem lokalen Restaurant',
      'Check-in im Hotel in Luxor',
      'Optionale Nilfahrt bei Sonnenuntergang',
      'Besuch des Luxor-Tempels am Abend',
      'Übernachtung im Hotel',
      'Tag 2: Frühstück im Hotel',
      'Besuch der Memnonkolosse',
      'Besuch des Hatschepsut-Tempels',
      'Besuch des Medinet-Habu-Tempels',
      'Mittagessen in einem lokalen Restaurant',
      'Besuch des Tals der Könige',
      'Rückfahrt nach Hurghada',
    ],
    itinerary_en: ['Day 1: Valley of the Kings, Hatshepsut Temple, Karnak Temple', 'Day 2: Luxor Temple, Felucca ride, Dendera (optional)'],
    included_de: ['Transfer', 'Übernachtung im Hotel', 'Frühstück im Hotel', '2 × Mittagessen', 'Deutschsprachiger Guide', 'Eintrittsgelder', 'Nilüberquerung (kurze Fährfahrt)', 'Mineralwasser und Softdrinks während der gesamten Tour im Fahrzeug verfügbar'],
    included_en: ['Transfer', 'Hotel', 'Guide', 'Lunch', 'Entrance fees'],
    excluded_de: ['Abendessen', 'Getränke', 'Trinkgeld'],
    excluded_en: ['Dinner', 'Drinks', 'Tips'],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [
      { question_de: 'Welches Hotel wird verwendet?', question_en: 'Which hotel is used?', answer_de: 'Die Übernachtung erfolgt in einem sorgfältig ausgewählten Hotel in Luxor. Die genaue Unterkunft kann je nach Verfügbarkeit variieren, entspricht jedoch stets einem guten Standard mit Frühstück.', answer_en: 'The overnight stay takes place in a carefully selected hotel in Luxor. The exact accommodation may vary depending on availability, but always meets a good standard with breakfast included.' },
      { question_de: 'Ist die Nilfahrt inklusive?', question_en: 'Is the Nile boat ride included?', answer_de: 'Die kurze Nilüberquerung zwischen Ost- und Westufer ist im Preis enthalten. Eine längere Nilfahrt bei Sonnenuntergang ist optional und kann vor Ort hinzugebucht werden.', answer_en: 'The short Nile crossing between the East and West Bank is included in the price. A longer sunset Nile boat ride is optional and can be booked on site.' },
      { question_de: 'Sind Getränke inklusive?', question_en: 'Are drinks included?', answer_de: 'Mineralwasser und Softdrinks stehen während der gesamten Tour im Fahrzeug kostenlos zur Verfügung. Getränke während der Mahlzeiten sind nicht im Preis enthalten.', answer_en: 'Mineral water and soft drinks are available free of charge in the vehicle throughout the entire tour. Drinks during meals are not included in the price.' },
      { question_de: 'Ist die Tour für Kinder geeignet?', question_en: 'Is the tour suitable for children?', answer_de: 'Ja, die private 2-Tages-Tour eignet sich auch für Familien mit Kindern. Durch das flexible Programm können Pausen individuell angepasst werden.', answer_en: 'Yes, the private 2-day tour is also suitable for families with children. Thanks to the flexible program, breaks can be adjusted individually.' },
    ],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 290 },
      { persons: '3 Pers.', price: 275 },
      { persons: '4 Pers.', price: 265 },
      { persons: '5–6 Pers.', price: 255 },
      { persons: '7–8 Pers.', price: 245 },
    ],
    child_price: 140,
  },
  {
    id: 'hurghada-luxor-balloon',
    departure_city: 'hurghada',
    title_de: 'Luxor 2-Tagesausflug inkl. Heißluftballonfahrt',
    title_en: 'Luxor 2-Day Tour incl. Hot Air Balloon',
    description_de: `Erleben Sie Luxor auf ganz besondere Weise während einer exklusiven 2-Tages-Tour ab Hurghada inklusive Heißluftballonfahrt bei Sonnenaufgang. Diese einzigartige Reise verbindet die berühmtesten Sehenswürdigkeiten des alten Ägyptens mit einem unvergesslichen Flug über das Niltal.

Genießen Sie eine Übernachtung in Luxor und entdecken Sie gemeinsam mit Ihrem deutschsprachigen Ägyptologen die faszinierende Welt der Pharaonen. Zu den Höhepunkten gehören der Karnak-Tempel, das Tal der Könige, der Tempel der Königin Hatschepsut, die Memnonkolosse sowie weitere bedeutende Monumente am Westufer des Nils.

Das absolute Highlight der Reise ist die Ballonfahrt bei Sonnenaufgang. Aus der Vogelperspektive erleben Sie das Tal der Könige, den Hatschepsut-Tempel, die Memnonkolosse, den Medinet-Habu-Tempel und die beeindruckende Landschaft Luxors aus einer völlig neuen Perspektive.

Diese private Tour ist ideal für Reisende, die Ägyptens Geschichte intensiv erleben möchten und dabei Wert auf Komfort, Flexibilität und persönliche Betreuung legen.`,
    description_en: 'Unique experience: Luxor highlights plus balloon ride over the Nile Valley. Float over the ancient sites at sunrise and experience Luxor from a bird\'s eye view.',
    price: 270,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/22be8f03187b04f2ae9113c2e976d310.jpeg',
    gallery: [
      '/images/hurghada/hurghada-luxor-balloon/1.jpg',
      '/images/hurghada/hurghada-luxor-balloon/2.jpg',
      '/images/hurghada/hurghada-luxor-balloon/3.jpg',
      '/images/hurghada/hurghada-luxor-balloon/4.jpg',
      '/images/hurghada/hurghada-luxor-balloon/5.jpg',
      '/images/hurghada/hurghada-luxor-balloon/6.jpg',
      '/images/hurghada/hurghada-luxor-balloon/7.jpg',
      '/images/hurghada/hurghada-luxor-balloon/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 156,
    highlights_de: ['Heißluftballonfahrt bei Sonnenaufgang über Luxor', 'Karnak-Tempel', 'Luxor-Tempel bei Nacht', 'Tal der Könige', 'Hatschepsut-Tempel', 'Memnonkolosse', 'Medinet-Habu-Tempel', 'Übernachtung im Hotel in Luxor', 'Frühstück im Hotel', 'Zwei Mittagessen während der Tour', 'Optionale Nilfahrt bei Sonnenuntergang', 'Privater klimatisierter Transfer', 'Deutschsprachiger Ägyptologe als Reiseleiter', 'Kleine Gruppen und persönlicher Service', 'Kostenlose Wasserflaschen und Softdrinks während der gesamten 2-tägigen Reise im Fahrzeug'],
    highlights_en: ['Hot air balloon at sunrise', 'Valley of the Kings', 'Karnak Temple', 'Hatshepsut Temple', 'Overnight stay', 'Private guide'],
    itinerary_de: ['Tag 1: • Abholung vom Hotel in Hurghada • Besuch des Karnak-Tempels • Mittagessen in einem lokalen Restaurant • Check-in im Hotel in Luxor • Optionale Nilfahrt bei Sonnenuntergang • Besuch des Luxor-Tempels am Abend • Übernachtung in Luxor', 'Tag 2: • Frühe Abholung zur Heißluftballonfahrt bei Sonnenaufgang • Rückkehr zum Hotel und Frühstück • Besuch der Memnonkolosse • Besuch des Hatschepsut-Tempels • Besuch des Medinet-Habu-Tempels • Mittagessen in einem lokalen Restaurant • Besuch des Tals der Könige • Rückfahrt nach Hurghada'],
    itinerary_en: ['Day 1: Arrival, Karnak Temple, Luxor Temple, overnight', 'Day 2: Sunrise balloon ride, Valley of the Kings, Hatshepsut Temple, return'],
    included_de: ['Privater klimatisierter Transfer ab/bis Hurghada', 'Übernachtung im Hotel in Luxor inklusive Frühstück', 'Heißluftballonfahrt bei Sonnenaufgang', 'Deutschsprachiger Ägyptologe als Reiseleiter', 'Eintrittsgelder laut Programm', 'Karnak-Tempel', 'Luxor-Tempel', 'Tal der Könige', 'Hatschepsut-Tempel', 'Memnonkolosse', 'Medinet-Habu-Tempel', 'Zwei Mittagessen während der Tour', 'Nilüberquerung zwischen Ost- und Westufer', 'Kostenlose Wasserflaschen und Softdrinks während der gesamten Reise im Fahrzeug'],
    included_en: ['Transfers', 'Hotel', 'Balloon ride', 'Guide', 'Lunch', 'Entrance fees'],
    excluded_de: ['Getränke in Restaurants', 'Optionale Nilfahrt bei Sonnenuntergang', 'Persönliche Ausgaben', 'Trinkgelder', 'Nicht erwähnte optionale Aktivitäten'],
    excluded_en: ['Dinner', 'Drinks', 'Tips'],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [
      { question_de: 'Ist die Heißluftballonfahrt im Preis enthalten?', question_en: 'Is the hot air balloon ride included in the price?', answer_de: 'Ja. Die Heißluftballonfahrt bei Sonnenaufgang über Luxor ist bereits im Reisepreis enthalten.', answer_en: 'Yes. The hot air balloon ride at sunrise over Luxor is already included in the tour price.' },
      { question_de: 'Welches Hotel wird genutzt?', question_en: 'Which hotel is used?', answer_de: 'Die Übernachtung erfolgt in einem sorgfältig ausgewählten Hotel in Luxor inklusive Frühstück.', answer_en: 'The overnight stay takes place in a carefully selected hotel in Luxor including breakfast.' },
      { question_de: 'Sind Getränke inklusive?', question_en: 'Are drinks included?', answer_de: 'Wasser und Softdrinks stehen während der gesamten Reise kostenlos im Fahrzeug zur Verfügung. Getränke in Restaurants sind nicht im Preis enthalten.', answer_en: 'Water and soft drinks are available free of charge in the vehicle throughout the entire trip. Drinks in restaurants are not included in the price.' },
      { question_de: 'Ist die Nilfahrt inklusive?', question_en: 'Is the Nile boat ride included?', answer_de: 'Die kurze Nilüberquerung zwischen Ost- und Westufer ist inklusive. Eine längere Nilfahrt bei Sonnenuntergang kann optional vor Ort gebucht werden.', answer_en: 'The short Nile crossing between the East and West Bank is included. A longer sunset Nile boat ride can be booked optionally on site.' },
      { question_de: 'Ist die Tour privat?', question_en: 'Is the tour private?', answer_de: 'Ja. Die Tour wird mit privatem Fahrzeug und deutschsprachigem Ägyptologen durchgeführt und bietet maximale Flexibilität ohne Massentourismus.', answer_en: 'Yes. The tour is conducted with a private vehicle and a German-speaking Egyptologist and offers maximum flexibility without mass tourism.' },
    ],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 350 },
      { persons: '3 Pers.', price: 310 },
      { persons: '4 Pers.', price: 300 },
      { persons: '5–6 Pers.', price: 290 },
      { persons: '7–8 Pers.', price: 270 },
    ],
    child_price: 185,
  },
  {
    id: 'hurghada-abydos-dendera',
    departure_city: 'hurghada',
    title_de: 'Abydos & Dendera Tempeln',
    title_en: 'Abydos & Dendera Temples',
    description_de: 'Entdecken Sie zwei der beeindruckendsten und zugleich weniger bekannten Tempelanlagen Ägyptens auf einem exklusiven privaten Tagesausflug ab Hurghada. Gemeinsam mit Ihrem deutschsprachigen Ägyptologen besuchen Sie zunächst den faszinierenden Hathor-Tempel in Dendera – einen der am besten erhaltenen Tempel Ägyptens mit seinen berühmten astronomischen Deckenreliefs, geheimnisvollen Krypten und der einzigartigen Darstellung von Königin Kleopatra. Anschließend reisen Sie nach Abydos, einer der bedeutendsten religiösen Stätten des alten Ägyptens und dem Kultzentrum des Gottes Osiris. Hier erkunden Sie den prachtvollen Tempel von Sethos I. mit seinen außergewöhnlich gut erhaltenen Reliefs, farbenprächtigen Hieroglyphen und der berühmten Königsliste von Abydos, die zu den wichtigsten historischen Quellen der ägyptischen Geschichte zählt. Während der gesamten Tour reisen Sie komfortabel in einem privaten, klimatisierten Fahrzeug. Ein ausgewähltes Mittagessen, deutschsprachige Reiseleitung sowie Wasser und Softdrinks im Fahrzeug sind inklusive. Diese Tour ist ideal für Reisende, die Ägyptens Geschichte abseits des Massentourismus intensiv erleben möchten.',
    description_en: 'Two of the most beautiful temple complexes in Egypt in one day. Visit the sacred temple of Abydos with the famous King List and the beautifully preserved Hathor Temple in Dendera.',
    price: 100,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/9428663c62b5a0ad6be40f78bbca194b.jpeg',
    gallery: [
      '/images/hurghada/hurghada-abydos-dendera/1.jpg',
      '/images/hurghada/hurghada-abydos-dendera/2.jpg',
      '/images/hurghada/hurghada-abydos-dendera/3.jpg',
      '/images/hurghada/hurghada-abydos-dendera/4.jpg',
      '/images/hurghada/hurghada-abydos-dendera/5.jpg',
      '/images/hurghada/hurghada-abydos-dendera/6.jpg',
      '/images/hurghada/hurghada-abydos-dendera/7.jpg',
      '/images/hurghada/hurghada-abydos-dendera/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 134,
    highlights_de: ['Hathor-Tempel von Dendera', 'Astronomische Deckenreliefs', 'Krypten und historische Inschriften', 'Darstellung von Königin Kleopatra', 'Tempel von Sethos I. in Abydos', 'Königsliste von Abydos', 'Kultstätte des Gottes Osiris', 'Hervorragend erhaltene Reliefs und Hieroglyphen', 'Deutschsprachiger Ägyptologe', 'Privater klimatisierter Transfer'],
    highlights_en: ['Abydos Temple', 'King List of Seti I', 'Dendera Hathor Temple', 'Zodiac ceiling', 'Well-preserved reliefs'],
    itinerary_de: ['05:00 – Abholung von Ihrem Hotel in Hurghada', 'Fahrt nach Abydos in einem komfortablen, klimatisierten Privatfahrzeug.', 'Besichtigung des beeindruckenden Tempels von Sethos I., der berühmten Königsliste von Abydos sowie des Osireion, das eng mit dem Kult des Gottes Osiris verbunden ist.', 'Mittagessen in einem ausgewählten lokalen Restaurant.', 'Weiterfahrt nach Dendera.', 'Besichtigung des hervorragend erhaltenen Hathor-Tempels mit seinen farbenprächtigen Reliefs, der astronomischen Decke, den Krypten und der berühmten Darstellung von Königin Kleopatra VII.', 'Freie Zeit für Fotos und Erkundung der Tempelanlage.', 'Rückfahrt nach Hurghada.', 'Ankunft im Hotel am Abend.'],
    itinerary_en: ['05:00 — Pickup', 'Drive to Abydos', 'Temple visit', 'Lunch', 'Dendera Temple', 'Return to Hurghada'],
    included_de: ['Privater klimatisierter Transfer ab/bis Hurghada', 'Deutschsprachiger Ägyptologe als Reiseleiter', 'Eintrittsgelder für alle genannten Sehenswürdigkeiten', 'Besichtigung des Abydos-Tempels', 'Besichtigung des Hathor-Tempels in Dendera', 'Mittagessen in einem lokalen Restaurant', 'Mineralwasser und Softdrinks im Fahrzeug', 'Alle lokalen Steuern und Servicegebühren'],
    included_en: ['Private air-conditioned transfer from/to Hurghada', 'English-speaking Egyptologist as guide', 'Entrance fees for all mentioned sights', 'Visit to Abydos Temple', 'Visit to Hathor Temple in Dendera', 'Lunch at a local restaurant', 'Mineral water and soft drinks in the vehicle', 'All local taxes and service charges'],
    excluded_de: ['Getränke im Restaurant', 'Persönliche Ausgaben', 'Trinkgelder', 'Nicht erwähnte optionale Aktivitäten oder Zusatzleistungen'],
    excluded_en: ['Drinks at the restaurant', 'Personal expenses', 'Tips', 'Unmentioned optional activities or additional services'],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 150 },
      { persons: '3 Pers.', price: 130 },
      { persons: '4 Pers.', price: 120 },
      { persons: '5–6 Pers.', price: 110 },
      { persons: '7–8 Pers.', price: 100 },
    ],
    child_price: 60,
  },
  {
    id: 'hurghada-dendera-half',
    departure_city: 'hurghada',
    title_de: 'Dendera Tempel (halber Tag)',
    title_en: 'Dendera Temple (Half Day)',
    description_de: 'Erleben Sie einen faszinierenden privaten Halbtagesausflug zum berühmten Hathor-Tempel von Dendera, einer der am besten erhaltenen Tempelanlagen des alten Ägyptens. Gemeinsam mit Ihrem deutschsprachigen Ägyptologen entdecken Sie die beeindruckenden Reliefs, die farbenprächtigen Deckenmalereien, die berühmte astronomische Tierkreisdecke sowie die einzigartige Darstellung der Königin Kleopatra VII.\n\nDer Tempel ist der Göttin Hathor gewidmet – der Göttin der Liebe, Musik, Freude und Mutterschaft – und zählt zu den beeindruckendsten Sehenswürdigkeiten Oberägyptens. Während der gesamten Tour reisen Sie komfortabel in einem klimatisierten Privatfahrzeug und genießen eine persönliche Betreuung ohne Massentourismus.\n\nIdeal für Reisende, die einen kürzeren kulturellen Ausflug mit außergewöhnlichen Einblicken in die altägyptische Geschichte erleben möchten.',
    description_en: 'Short excursion to the Hathor Temple in Dendera. Ideal for travelers looking for a short but impressive archaeological trip — with the famous zodiac ceiling and colorful reliefs.',
    price: 90,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/66e8559a224b06f014b8f7b359c071e5.jpeg',
    gallery: [
      '/images/hurghada/hurghada-dendera-half/1.jpg',
      '/images/hurghada/hurghada-dendera-half/2.jpg',
      '/images/hurghada/hurghada-dendera-half/3.jpg',
      '/images/hurghada/hurghada-dendera-half/4.jpg',
      '/images/hurghada/hurghada-dendera-half/5.jpg',
      '/images/hurghada/hurghada-dendera-half/6.jpg',
      '/images/hurghada/hurghada-dendera-half/7.jpg',
      '/images/hurghada/hurghada-dendera-half/8.jpg',
    ],
    rating: 4.6,
    reviews_count: 89,
    highlights_de: ['Hathor-Tempel von Dendera', 'Astronomische Tierkreisdecke', 'Darstellung der Königin Kleopatra VII.', 'Farbenprächtige Reliefs und Hieroglyphen', 'Besuch der Krypten des Tempels', 'Heiliger See und Mammisi', 'Deutschsprachiger Ägyptologe', 'Privater klimatisierter Transfer', 'Individuelle Betreuung ohne Massentourismus', 'Faszinierende Einblicke in Religion, Astronomie und Geschichte des alten Ägyptens'],
    highlights_en: ['Hathor Temple', 'Zodiac ceiling', 'Colored reliefs', 'Crypts', 'Short excursion'],
    itinerary_de: ['06:00 Uhr – Abholung von Ihrem Hotel in Hurghada', 'Fahrt nach Dendera in einem komfortablen, klimatisierten Privatfahrzeug.', 'Ankunft am Hathor-Tempel von Dendera und ausführliche Besichtigung mit Ihrem deutschsprachigen Ägyptologen.', 'Besichtigung des Haupttempels, der astronomischen Tierkreisdecke, der Krypten, des Heiligen Sees, des Mammisi sowie der berühmten Darstellung von Königin Kleopatra VII.', 'Freie Zeit für Fotos und individuelle Erkundung der Tempelanlage.', 'Mineralwasser und Softdrinks stehen während der gesamten Fahrt im Fahrzeug zur Verfügung.', 'Rückfahrt nach Hurghada.', 'Ankunft im Hotel gegen Mittag.'],
    itinerary_en: ['06:00 — Pickup', 'Drive to Qena', 'Dendera Temple', 'Return', 'Noon — Arrival'],
    included_de: ['Privater klimatisierter Transfer ab/bis Hurghada', 'Deutschsprachiger Ägyptologe als Reiseleiter', 'Eintrittsgelder für alle genannten Sehenswürdigkeiten', 'Besichtigung des Hathor-Tempels von Dendera', 'Besuch des Heiligen Sees', 'Besuch des Mammisi (Geburtshaus)', 'Besichtigung der Krypten und der astronomischen Tierkreisdecke', 'Darstellung der Königin Kleopatra VII.', 'Mittagessen in einem lokalen Restaurant', 'Mineralwasser und Softdrinks im Fahrzeug', 'Alle lokalen Steuern und Servicegebühren'],
    included_en: ['Private air-conditioned transfer from/to Hurghada', 'English-speaking Egyptologist as guide', 'Entrance fees for all mentioned sights', 'Visit to Hathor Temple of Dendera', 'Visit to the Sacred Lake', 'Visit to the Mammisi (Birth House)', 'Visit to the crypts and the astronomical zodiac ceiling', 'Depiction of Queen Cleopatra VII', 'Lunch at a local restaurant', 'Mineral water and soft drinks in the vehicle', 'All local taxes and service charges'],
    excluded_de: ['Getränke im Restaurant', 'Persönliche Ausgaben', 'Trinkgelder', 'Nicht erwähnte optionale Aktivitäten oder Zusatzleistungen'],
    excluded_en: ['Drinks at the restaurant', 'Personal expenses', 'Tips', 'Unmentioned optional activities or additional services'],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 110 },
      { persons: '3 Pers.', price: 105 },
      { persons: '4 Pers.', price: 100 },
      { persons: '5–6 Pers.', price: 95 },
      { persons: '7–8 Pers.', price: 90 },
    ],
    child_price: 45,
  },
  {
    id: 'hurghada-cairo-1day',
    departure_city: 'hurghada',
    title_de: 'Tagesausflug nach Kairo',
    title_en: 'Cairo Day Tour',
    description_de: 'Erleben Sie die faszinierenden Höhepunkte Kairos auf einem exklusiven privaten Tagesausflug von Hurghada. Gemeinsam mit Ihrem deutschsprachigen Ägyptologen entdecken Sie die weltberühmten Pyramiden von Gizeh, die geheimnisvolle Sphinx und das beeindruckende Grand Egyptian Museum mit seinen einzigartigen Schätzen der altägyptischen Zivilisation.\n\nTauchen Sie ein in die Geschichte der Pharaonen und bestaunen Sie einige der bedeutendsten Monumente der Menschheit. Während der gesamten Tour reisen Sie komfortabel in einem modernen, klimatisierten Privatfahrzeug und genießen persönliche Betreuung ohne Massentourismus.\n\nEin Mittagessen in einem ausgewählten lokalen Restaurant, Mineralwasser und Softdrinks im Fahrzeug sowie ausreichend Zeit für Besichtigungen und Fotos machen diesen Ausflug zu einem unvergesslichen Erlebnis. Ideal für Reisende, die Kairo individuell, komfortabel und mit fundierten Erklärungen eines deutschsprachigen Ägyptologen erleben möchten.',
    description_en: 'Pyramids of Giza, Sphinx & Museum – a day full of impressions. Experience the fascinating capital of Egypt with its ancient wonders and the famous Egyptian Museum.',
    price: 120,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/f6cc0fe247f3a6b7bc1aa3f51e8b4bcd.jpeg',
    gallery: [
      '/images/hurghada/hurghada-cairo-1day/1.jpg',
      '/images/hurghada/hurghada-cairo-1day/2.jpg',
      '/images/hurghada/hurghada-cairo-1day/3.jpg',
      '/images/hurghada/hurghada-cairo-1day/4.jpg',
      '/images/hurghada/hurghada-cairo-1day/5.jpg',
      '/images/hurghada/hurghada-cairo-1day/6.jpg',
      '/images/hurghada/hurghada-cairo-1day/7.jpg',
      '/images/hurghada/hurghada-cairo-1day/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 445,
    highlights_de: ['Pyramiden von Gizeh', 'Große Sphinx', 'Grand Egyptian Museum (GEM)', 'Schätze des alten Ägyptens', 'Deutschsprachiger Ägyptologe', 'Privater Transfer', 'Mittagessen inklusive', 'Mineralwasser & Softdrinks im Fahrzeug'],
    highlights_en: ['Pyramids of Giza', 'Sphinx', 'Egyptian Museum', 'Tutankhamun Treasures', 'Private Guide'],
    itinerary_de: ['02:00 Uhr – Abholung von Ihrem Hotel in Hurghada. Fahrt nach Kairo in einem komfortablen, klimatisierten Privatfahrzeug. Während der Fahrt stehen Mineralwasser und Softdrinks im Fahrzeug zur Verfügung.', '08:00 Uhr – Ankunft auf dem Gizeh-Plateau. Besichtigung der weltberühmten Pyramiden von Cheops, Chephren und Mykerinos. Ihr deutschsprachiger Ägyptologe erklärt Ihnen die faszinierende Geschichte dieser einzigartigen Bauwerke. Optional besteht die Möglichkeit, eine der Pyramiden von innen zu besichtigen.', '10:30 Uhr – Besuch der Großen Sphinx. Besichtigung der legendären Sphinx sowie des Taltempels von Chephren mit ausreichend Zeit für Fotos und Erkundungen.', '12:30 Uhr – Mittagessen in einem ausgewählten lokalen Restaurant mit orientalischen Spezialitäten.', '14:00 Uhr – Grand Egyptian Museum (GEM). Besichtigung des beeindruckenden Grand Egyptian Museum mit seinen monumentalen Ausstellungsstücken und den bedeutendsten Schätzen der altägyptischen Geschichte.', '16:30 Uhr – Rückfahrt nach Hurghada. Entspannte Rückfahrt im klimatisierten Privatfahrzeug.', '23:00 Uhr – Ankunft im Hotel.'],
    itinerary_en: ['02:00 — Pickup in Hurghada', '08:00 — Pyramids & Sphinx', '12:00 — Lunch', '14:00 — Egyptian Museum', '16:00 — Return', '23:00 — Hotel arrival'],
    included_de: ['Privater klimatisierter Transfer ab/bis Hurghada', 'Deutschsprachiger Ägyptologe als Reiseleiter', 'Eintrittsgelder für alle genannten Sehenswürdigkeiten', 'Pyramiden von Gizeh', 'Sphinx', 'Taltempel von Chephren', 'Grand Egyptian Museum (GEM)', 'Mittagessen in einem lokalen Restaurant', 'Mineralwasser und Softdrinks im Fahrzeug', 'Alle lokalen Steuern und Servicegebühren'],
    included_en: ['Transfer', 'Guide', 'Lunch', 'Entrance fees'],
    excluded_de: ['Getränke im Restaurant', 'Eintritt in die Grabkammern der Pyramiden (optional, vor Ort zahlbar)', 'Persönliche Ausgaben', 'Trinkgelder', 'Nicht erwähnte optionale Aktivitäten oder Zusatzleistungen'],
    excluded_en: ['Drinks', 'Camel ride (optional)', 'Tips'],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [
      { question_de: 'Wie lange dauert die Fahrt von Hurghada nach Kairo?', question_en: 'How long is the drive from Hurghada to Cairo?', answer_de: 'Die Fahrt von Hurghada nach Kairo dauert je nach Verkehrslage etwa 5 bis 6 Stunden pro Strecke. Die Reise erfolgt in einem modernen, klimatisierten Privatfahrzeug.', answer_en: 'The drive from Hurghada to Cairo takes about 5 to 6 hours each way, depending on traffic. The journey takes place in a modern, air-conditioned private vehicle.' },
      { question_de: 'Ist der Eintritt in die Pyramiden inklusive?', question_en: 'Is the entrance to the pyramids included?', answer_de: 'Der Besuch des Gizeh-Plateaus und der Pyramiden von außen ist inklusive. Der Eintritt in die Grabkammern der Pyramiden ist optional und kann vor Ort separat bezahlt werden.', answer_en: 'The visit to the Giza Plateau and the pyramids from the outside is included. Entrance to the burial chambers of the pyramids is optional and can be paid separately on site.' },
      { question_de: 'Ist das Grand Egyptian Museum im Preis enthalten?', question_en: 'Is the Grand Egyptian Museum included in the price?', answer_de: 'Ja, der Eintritt in das Grand Egyptian Museum (GEM) ist im Tourpreis enthalten.', answer_en: 'Yes, the entrance to the Grand Egyptian Museum (GEM) is included in the tour price.' },
      { question_de: 'Sind Getränke inklusive?', question_en: 'Are drinks included?', answer_de: 'Mineralwasser und Softdrinks im Fahrzeug sind während der gesamten Tour inklusive. Getränke im Restaurant sind nicht im Preis enthalten.', answer_en: 'Mineral water and soft drinks in the vehicle are included throughout the entire tour. Drinks at the restaurant are not included in the price.' },
      { question_de: 'Ist die Tour privat?', question_en: 'Is the tour private?', answer_de: 'Ja, dies ist eine private Tour. Sie reisen ausschließlich mit Ihrer eigenen Gruppe und werden von einem deutschsprachigen Ägyptologen begleitet.', answer_en: 'Yes, this is a private tour. You travel exclusively with your own group and are accompanied by a German-speaking Egyptologist.' },
    ],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 170 },
      { persons: '3 Pers.', price: 160 },
      { persons: '4 Pers.', price: 140 },
      { persons: '5–6 Pers.', price: 130 },
      { persons: '7–8 Pers.', price: 120 },
    ],
    child_price: 60,
  },
  {
    id: 'hurghada-cairo-2day',
    departure_city: 'hurghada',
    title_de: '2-Tagesausflug nach Kairo',
    title_en: 'Cairo 2-Day Tour',
    description_de: `Erleben Sie die Höhepunkte Ägyptens auf einer exklusiven privaten 2-Tages-Tour von Hurghada nach Kairo. Entdecken Sie die faszinierende Geschichte der Pharaonen, islamische Architektur und das moderne Grand Egyptian Museum in Ihrem eigenen Tempo und fernab vom Massentourismus.

Gemeinsam mit Ihrem persönlichen deutschsprachigen Ägyptologen besuchen Sie die weltberühmten Pyramiden von Gizeh, die Große Sphinx und den Taltempel. Darüber hinaus erkunden Sie die Stufenpyramide von Sakkara, das beeindruckende Grand Egyptian Museum (GEM), die berühmte Zitadelle von Saladin mit der Mohamed-Ali-Moschee sowie die historischen Viertel Kairos mit der El-Moaz-Straße und dem Khan El Khalili Basar.

Ein besonderes Highlight dieser Reise ist die Übernachtung in einem sorgfältig ausgewählten Hotel in Kairo inklusive Frühstück. Während der gesamten Tour genießen Sie private Transfers, persönliche Betreuung, maximale Flexibilität und den Komfort einer individuell organisierten Reise ohne Verkaufsveranstaltungen oder große Reisegruppen.

Diese private 2-Tages-Tour ist die perfekte Wahl für alle, die die wichtigsten Sehenswürdigkeiten Kairos intensiv erleben und gleichzeitig höchsten Wert auf Qualität, Komfort und persönliche Betreuung legen.`,
    description_en: 'More time in Cairo – two days with overnight stay & museum visits. Discover Cairo at leisure with pyramids, museum, citadel and Khan El Khalili Bazaar.',
    price: 260,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/47fb58eace81efa8db0495ed3e85f331.jpeg',
    gallery: [
      '/images/hurghada/hurghada-cairo-2day/1.jpg',
      '/images/hurghada/hurghada-cairo-2day/2.jpg',
      '/images/hurghada/hurghada-cairo-2day/3.jpg',
      '/images/hurghada/hurghada-cairo-2day/4.jpg',
      '/images/hurghada/hurghada-cairo-2day/5.jpg',
      '/images/hurghada/hurghada-cairo-2day/6.jpg',
      '/images/hurghada/hurghada-cairo-2day/7.jpg',
      '/images/hurghada/hurghada-cairo-2day/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 167,
    highlights_de: [
      'Privater 2-Tages-Ausflug von Hurghada nach Kairo',
      'Deutschsprachiger Ägyptologe während der gesamten Reise',
      'Pyramiden von Gizeh (Cheops, Chephren & Mykerinos)',
      'Große Sphinx und Taltempel',
      'Grand Egyptian Museum (GEM)',
      'Stufenpyramide von Sakkara',
      'Zitadelle von Saladin',
      'Mohamed-Ali-Moschee',
      'Historische El-Moaz-Straße',
      'Khan El Khalili Basar',
      'Übernachtung im Hotel in Kairo inklusive Frühstück',
      'Zwei Mittagessen während der Tour',
      'Softdrinks und Mineralwasser während der Fahrten',
      'Private klimatisierte Transfers',
      'Keine Verkaufsveranstaltungen und kein Massentourismus'
    ],
    highlights_en: ['Pyramids & Sphinx', 'Egyptian Museum', 'Salah El Din Citadel', 'Khan El Khalili Bazaar', 'Hotel overnight in Cairo'],
    itinerary_de: ['Tag 1: Abholung in Hurghada, Grand Egyptian Museum (GEM), Mittagessen, Pyramiden von Gizeh, Große Sphinx, Stufenpyramide von Sakkara, Khan El Khalili Basar, Übernachtung im Hotel in Kairo',
'Tag 2: Frühstück im Hotel, Zitadelle von Salah El Din, Mohamed-Ali-Moschee, Alt-Kairo, Mittagessen, Rückfahrt nach Hurghada'],
    itinerary_en: [
'Day 1: Pickup in Hurghada, Grand Egyptian Museum (GEM), Lunch, Giza Pyramids, Great Sphinx, Saqqara Step Pyramid, Khan El Khalili Bazaar, Overnight stay in a Cairo hotel',
'Day 2: Breakfast at the hotel, Salah El Din Citadel, Mohamed Ali Mosque, Old Cairo, Lunch, Return to Hurghada'
],
    included_de: [
'Private Transfers in klimatisierten Fahrzeugen',
'Übernachtung im Hotel in Kairo',
'Frühstück im Hotel',
'Deutschsprachiger Ägyptologe',
'Mittagessen an beiden Tagen',
'Alle Eintrittsgebühren laut Programm',
'Grand Egyptian Museum (GEM)',
'Pyramiden von Gizeh',
'Große Sphinx',
'Stufenpyramide von Sakkara',
'Khan El Khalili Basar',
'Zitadelle von Salah El Din',
'Mohamed-Ali-Moschee',
'Alt-Kairo',
'Softdrinks während der Fahrten',
'Alle lokalen Steuern und Servicegebühren'
],
    included_en: [
'Private transfers in air-conditioned vehicles',
'Overnight stay in a Cairo hotel',
'Breakfast at the hotel',
'English-speaking Egyptologist guide',
'Lunch on both days',
'All entrance fees according to the itinerary',
'Grand Egyptian Museum (GEM)',
'Giza Pyramids',
'Great Sphinx',
'Saqqara Step Pyramid',
'Khan El Khalili Bazaar',
'Salah El Din Citadel',
'Mohamed Ali Mosque',
'Old Cairo',
'Soft drinks during transfers',
'All local taxes and service charges'
],
    excluded_de: [
'Abendessen',
'Getränke in Restaurants',
'Persönliche Ausgaben',
'Trinkgelder',
'Optionale Aktivitäten'
],
    excluded_en: [
'Dinner',
'Drinks in restaurants',
'Personal expenses',
'Tips',
'Optional activities'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 360 },
      { persons: '3 Pers.', price: 330 },
      { persons: '4 Pers.', price: 300 },
      { persons: '5–6 Pers.', price: 280 },
      { persons: '7–8 Pers.', price: 260 },
    ],
    child_price: 180,
  },
  {
    id: 'hurghada-cairo-flight-1day',
    departure_city: 'hurghada',
    title_de: 'Tagesausflug nach Kairo mit Flugzeug',
    title_en: 'Cairo Day Tour by Flight',
    description_de: 'Schneller & bequemer Tagestrip nach Kairo. Fliegen Sie von Hurghada nach Kairo und genießen Sie die Pyramiden, Sphinx, Museum und Basar ohne lange Busfahrt.',
    description_en: 'Quick & comfortable day trip to Cairo. Fly from Hurghada to Cairo and enjoy the Pyramids, Sphinx, Museum and Bazaar without a long bus ride.',
    price: 250,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/fa4ab020db1fb4e3feebbca27c26c7c6.jpeg',
    gallery: [
      '/images/hurghada/hurghada-cairo-flight-1day/1.jpg',
      '/images/hurghada/hurghada-cairo-flight-1day/2.jpg',
      '/images/hurghada/hurghada-cairo-flight-1day/3.jpg',
      '/images/hurghada/hurghada-cairo-flight-1day/4.jpg',
      '/images/hurghada/hurghada-cairo-flight-1day/5.jpg',
      '/images/hurghada/hurghada-cairo-flight-1day/6.jpg',
      '/images/hurghada/hurghada-cairo-flight-1day/7.jpg',
      '/images/hurghada/hurghada-cairo-flight-1day/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 245,
    highlights_de: [

'Flug von Hurghada nach Kairo und zurück',

'Deutschsprachiger Ägyptologe während des gesamten Ausflugs',

'Besichtigung der Pyramiden von Gizeh',

'Große Sphinx und Taltempel',

'Grand Egyptian Museum (GEM)',

'Mittagessen in einem lokalen Restaurant',

'Private Transfers in Hurghada und Kairo',

'Kein Massentourismus und keine Verkaufsveranstaltungen'

],
    highlights_en: [

'Round-trip flight from Hurghada to Cairo',

'German-speaking Egyptologist guide',

'Visit to the Giza Pyramids',

'Great Sphinx and Valley Temple',

'Grand Egyptian Museum (GEM)',

'Lunch at a local restaurant',

'Private transfers in Hurghada and Cairo',

'No shopping stops and no mass tourism'

],
    itinerary_de: [

'05:00 Abholung vom Hotel und Transfer zum Flughafen Hurghada',

'Flug nach Kairo',

'Besichtigung des Grand Egyptian Museum (GEM)',

'Mittagessen in einem ausgewählten Restaurant',

'Besichtigung der Pyramiden von Gizeh',

'Besichtigung der Großen Sphinx und des Taltempels',

'Freizeit und Fotostopps auf dem Pyramidenplateau',

'Transfer zum Flughafen Kairo',

'Rückflug nach Hurghada und Transfer zum Hotel'

],
    itinerary_en: [

'05:00 Hotel pickup and transfer to Hurghada Airport',

'Flight to Cairo',

'Visit the Grand Egyptian Museum (GEM)',

'Lunch at a selected local restaurant',

'Visit the Giza Pyramids',

'Visit the Great Sphinx and Valley Temple',

'Free time and photo stops on the Giza Plateau',

'Transfer to Cairo Airport',

'Flight back to Hurghada and transfer to your hotel'

],
    included_de: [
'Inlandsflug Hurghada – Kairo – Hurghada',
'Transfers',
'Deutschsprachiger Ägyptologe',
'Mittagessen',
'Eintrittsgebühren',
'Klimatisierte Fahrzeuge'
],
    included_en: [
'Domestic flight Hurghada – Cairo – Hurghada',
'Transfers',
'English-speaking Egyptologist',
'Lunch',
'Entrance fees',
'Air-conditioned vehicles'
],
    excluded_de: [
'Getränke im Restaurant',
'Persönliche Ausgaben',
'Trinkgelder'
],
    excluded_en: ['Drinks in the restaurant',
'Personal expenses',
'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada, Transfer zum Flughafen',
    meeting_point_en: 'Hotel pickup in Hurghada, transfer to airport',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 280 },
      { persons: '3 Pers.', price: 275 },
      { persons: '4 Pers.', price: 270 },
      { persons: '5–6 Pers.', price: 260 },
      { persons: '7–8 Pers.', price: 250 },
    ],
    child_price: 200,
  },
  {
    id: 'hurghada-cairo-flight-2day',
    departure_city: 'hurghada',
    title_de: '2-Tagesausflug nach Kairo mit Flugzeug',
    title_en: 'Cairo 2-Day Tour by Flight',
    description_de: 'Noch intensiver: 2 Tage Kairo inkl. Flügen & Hotel. Erleben Sie Kairo ausführlich mit Pyramiden, Museen, Zitadelle und Basar — bequem per Flugzeug.',
    description_en: 'Even more intensive: 2 days Cairo incl. flights & hotel. Experience Cairo in detail with pyramids, museums, citadel and bazaar — comfortably by plane.',
    price: 420,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/69f6062df6672f5d50788664473967c6.jpeg',
    gallery: [
      '/images/hurghada/hurghada-cairo-flight-2day/1.jpg',
      '/images/hurghada/hurghada-cairo-flight-2day/2.jpg',
      '/images/hurghada/hurghada-cairo-flight-2day/3.jpg',
      '/images/hurghada/hurghada-cairo-flight-2day/4.jpg',
      '/images/hurghada/hurghada-cairo-flight-2day/5.jpg',
      '/images/hurghada/hurghada-cairo-flight-2day/6.jpg',
      '/images/hurghada/hurghada-cairo-flight-2day/7.jpg',
      '/images/hurghada/hurghada-cairo-flight-2day/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 98,
    highlights_de: [
'Hin- und Rückflug zwischen Hurghada und Kairo',
'Privater 2-Tages-Ausflug mit deutschsprachigem Ägyptologen',
'Grand Egyptian Museum (GEM)',
'Pyramiden von Gizeh, Große Sphinx und Taltempel',
'Stufenpyramide von Sakkara',
'Übernachtung im Hotel inklusive Frühstück',
'Zitadelle von Salah El Din und Mohamed-Ali-Moschee',
'Alt-Kairo und Khan El Khalili Basar',
'Private Transfers ohne Verkaufsveranstaltungen'
],
    highlights_en: [
'Round-trip flights between Hurghada and Cairo',
'Private 2-day tour with a German-speaking Egyptologist',
'Grand Egyptian Museum (GEM)',
'Giza Pyramids, Great Sphinx and Valley Temple',
'Saqqara Step Pyramid',
'Hotel overnight stay with breakfast',
'Salah El Din Citadel and Mohamed Ali Mosque',
'Old Cairo and Khan El Khalili Bazaar',
'Private transfers with no shopping stops'
],
    itinerary_de: [
'Tag 1: Flug nach Kairo, Grand Egyptian Museum (GEM), Mittagessen, Pyramiden von Gizeh, Große Sphinx, Taltempel, Sakkara, Hotelübernachtung',
'Tag 2: Frühstück im Hotel, Zitadelle von Salah El Din, Mohamed-Ali-Moschee, Alt-Kairo, Mittagessen, Khan El Khalili Basar, Rückflug nach Hurghada'
],
    itinerary_en: [
'Day 1: Flight to Cairo, Grand Egyptian Museum (GEM), Lunch, Giza Pyramids, Great Sphinx, Valley Temple, Saqqara, Hotel overnight stay',
'Day 2: Breakfast at the hotel, Salah El Din Citadel, Mohamed Ali Mosque, Old Cairo, Lunch, Khan El Khalili Bazaar, Flight back to Hurghada'
],
    included_de: [
'Inlandsflüge Hurghada – Kairo – Hurghada',
'Alle Transfers',
'Hotelübernachtung mit Frühstück',
'Deutschsprachiger Ägyptologe',
'Mittagessen an beiden Tagen',
'Eintrittsgebühren',
'Klimatisierte Fahrzeuge'
],
    included_en:  [
'Domestic flights Hurghada – Cairo – Hurghada',
'All transfers',
'Hotel overnight stay with breakfast',
'English-speaking Egyptologist',
'Lunch on both days',
'Entrance fees',
'Air-conditioned vehicles'
],
    excluded_de:  [
'Abendessen',
'Getränke',
'Persönliche Ausgaben',
'Trinkgelder'
],
    excluded_en: [
'Dinner',
'Drinks',
'Personal expenses',
'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada, Transfer zum Flughafen',
    meeting_point_en: 'Hotel pickup in Hurghada, transfer to airport',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 520 },
      { persons: '3 Pers.', price: 490 },
      { persons: '4 Pers.', price: 470 },
      { persons: '5–6 Pers.', price: 430 },
      { persons: '7–8 Pers.', price: 420 },
    ],
    child_price: 300,
  },
  {
    id: 'hurghada-stadtrundfahrt',
    departure_city: 'hurghada',
    title_de: 'Stadtrundfahrt Hurghada',
    title_en: 'Hurghada City Tour',
    description_de: 'Entdecke Hurghada: Moschee, Hafen, Märkte & Marina. Lernen Sie die Stadt hinter den Resorts kennen mit ihren lebendigen Märkten und kulturellen Highlights.',
    description_en: 'Discover Hurghada: Mosque, harbor, markets & marina. Get to know the city behind the resorts with its lively markets and cultural highlights.',
    price: 20,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/8d64dddde5569a679acc60796e9a992b.jpeg',
    gallery: [
      '/images/hurghada/hurghada-stadtrundfahrt/1.jpg',
      '/images/hurghada/hurghada-stadtrundfahrt/2.jpg',
      '/images/hurghada/hurghada-stadtrundfahrt/3.jpg',
      '/images/hurghada/hurghada-stadtrundfahrt/4.jpg',
      '/images/hurghada/hurghada-stadtrundfahrt/5.jpg',
      '/images/hurghada/hurghada-stadtrundfahrt/6.jpg',
      '/images/hurghada/hurghada-stadtrundfahrt/7.jpg',
      '/images/hurghada/hurghada-stadtrundfahrt/8.jpg',
    ],
    rating: 4.5,
    reviews_count: 234,
    highlights_de:  [
'Private Stadtrundfahrt durch Hurghada',
'Besuch der El-Mina-Moschee',
'Spaziergang durch die Marina Hurghada',
'Besichtigung des Fischmarkts',
'Besuch des Obst- und Gemüsemarkts',
'Freizeit zum Einkaufen in den Souks',
'Deutschsprachiger Reiseleiter',
'Private klimatisierte Transfers'
],
    highlights_en: [
'Private Hurghada city tour',
'Visit El Mina Mosque',
'Walk through Hurghada Marina',
'Visit the local fish market',
'Explore the fruit and vegetable market',
'Free time for shopping in the local bazaars',
'English-speaking tour guide',
'Private air-conditioned transfers'
],
    itinerary_de: [
'Abholung vom Hotel',
'Besichtigung der El-Mina-Moschee',
'Besuch des Fischmarkts',
'Rundgang über den Obst- und Gemüsemarkt',
'Freizeit zum Einkaufen in den traditionellen Souks',
'Spaziergang entlang der Marina Hurghada',
'Rückfahrt zum Hotel'
],
    itinerary_en: [
'Hotel pickup',
'Visit El Mina Mosque',
'Explore the local fish market',
'Walk through the fruit and vegetable market',
'Free time for shopping in the traditional bazaars',
'Walk along Hurghada Marina',
'Return to the hotel'
],
    included_de:  [
'Transfers',
'Deutschsprachiger Reiseleiter',
'Klimatisiertes Fahrzeug',
'Mineralwasser und Softdrinks im Fahrzeug'
],
    included_en:  [
'Transfers',
'English-speaking guide',
'Air-conditioned vehicle',
'Mineral water and soft drinks during the transfer'
],
    excluded_de: [
'Persönliche Ausgaben',
'Trinkgelder'
],
    excluded_en: [
'Personal expenses',
'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 30 },
      { persons: '3 Pers.', price: 25 },
      { persons: '4 Pers.', price: 25 },
      { persons: '5–6 Pers.', price: 20 },
      { persons: '7–8 Pers.', price: 20 },
    ],
    child_price: 10,
  },
  {
    id: 'hurghada-elgouna',
    departure_city: 'hurghada',
    title_de: 'El Gouna Stadtrundfahrt',
    title_en: 'El Gouna City Tour',
    description_de: `Erleben Sie El Gouna auf einer exklusiven privaten Stadtrundfahrt ab Hurghada – die elegante Lagunenstadt am Roten Meer, die oft als das „Venedig Ägyptens“ bezeichnet wird. Gemeinsam mit Ihrem deutschsprachigen Reiseleiter entdecken Sie die schönsten Sehenswürdigkeiten der Stadt ganz entspannt und ohne Zeitdruck.

Während der Tour besuchen Sie die berühmte Abu Tig Marina mit ihren luxuriösen Yachten, eleganten Cafés und Restaurants sowie die lebendige Downtown von El Gouna mit ihren Geschäften und ihrer einzigartigen Architektur. Ein besonderes Highlight ist die romantische Lagunenfahrt, bei der Sie El Gouna aus einer völlig neuen Perspektive erleben und wunderschöne Erinnerungsfotos machen können.

Diese private Tour eignet sich perfekt für alle, die El Gouna individuell kennenlernen möchten – ohne große Gruppen, ohne Verkaufsveranstaltungen und mit persönlicher Betreuung durch Yallabina Tours. Genießen Sie eine entspannte Atmosphäre, flexible Gestaltung und einen unvergesslichen Ausflug an einem der schönsten Orte am Roten Meer.`,
    description_en: `Discover El Gouna on an exclusive private city tour from Hurghada and experience one of the most beautiful and elegant destinations on the Red Sea. Often called the "Venice of Egypt", El Gouna is famous for its crystal-clear lagoons, luxurious marina, charming downtown and unique architecture. Together with your German-speaking Egyptologist, you will explore the city in a relaxed atmosphere without any time pressure.

Visit the famous Abu Tig Marina with its luxury yachts, cafés and restaurants, stroll through the lively downtown area and enjoy a peaceful lagoon boat ride offering breathtaking views of the city from the water. Along the way, there will be plenty of opportunities to take unforgettable photos and experience the special charm of El Gouna.

This private excursion is ideal for travelers who prefer a personalized experience instead of crowded group tours. With Yallabina Tours, you enjoy flexible service, personal attention, no unwanted shopping stops and an unforgettable day discovering the unique beauty of El Gouna.`,
    price: 30,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/7d74ace9d6c517f571cedb8ba06b85c6.jpeg',
    gallery: [
      '/images/hurghada/hurghada-elgouna/1.jpg',
      '/images/hurghada/hurghada-elgouna/2.jpg',
      '/images/hurghada/hurghada-elgouna/3.jpg',
      '/images/hurghada/hurghada-elgouna/4.jpg',
      '/images/hurghada/hurghada-elgouna/5.jpg',
      '/images/hurghada/hurghada-elgouna/6.jpg',
      '/images/hurghada/hurghada-elgouna/7.jpg',
      '/images/hurghada/hurghada-elgouna/8.jpg',
    ],
    rating: 4.6,
    reviews_count: 178,
    highlights_de: [
'Private Stadtrundfahrt durch El Gouna',
'Deutschsprachiger Reiseleiter',
'Panoramablick vom Aussichtsturm',
'Besichtigung der Abu Tig Marina',
'Entspannte Lagunen-Bootsfahrt',
'Spaziergang durch Downtown El Gouna',
'Zeit für Fotos und Freizeit',
'Keine Verkaufsveranstaltungen'
],
    highlights_en: [
'Private El Gouna city tour',
'English-speaking guide',
'Panoramic views from the observation tower',
'Visit to Abu Tig Marina',
'Relaxing lagoon boat ride',
'Walk through Downtown El Gouna',
'Free time for photos',
'No shopping stops'
],
    itinerary_de: [
' Abholung von Ihrem Hotel in Hurghada',
'Fahrt nach El Gouna',
'Panoramablick vom Aussichtsturm',
'Spaziergang durch Downtown El Gouna',
'Besichtigung der luxuriösen Abu Tig Marina',
'Entspannte Lagunen-Bootsfahrt',
'Freie Zeit für Fotos und eigene Erkundungen',
'Rückfahrt zu Ihrem Hotel in Hurghada'
],
    itinerary_en: [
'  Hotel pickup in Hurghada',
'Drive to El Gouna',
'Enjoy panoramic views from the observation tower',
'Walk through Downtown El Gouna',
'Visit the luxurious Abu Tig Marina',
'Relaxing lagoon boat ride',
'Free time for photos and personal exploration',
'Return to your hotel in Hurghada'
],
    included_de: ['Transfers', 'Bootsfahrt', 'Guide'],
    included_en: ['Transfers', 'Boat ride', 'Guide'],
    excluded_de: ['Mahlzeiten', 'Einkäufe', 'Trinkgeld'],
    excluded_en: ['Meals', 'Shopping', 'Tips'],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 50 },
      { persons: '3 Pers.', price: 45 },
      { persons: '4 Pers.', price: 40 },
      { persons: '5–6 Pers.', price: 35 },
      { persons: '7–8 Pers.', price: 30 },
    ],
    child_price: 20,
  },
  {
    id: 'hurghada-st-antonius-paulus',
    departure_city: 'hurghada',
    title_de: 'St. Antonius & Paulus Klöster',
    title_en: 'St. Anthony & Paul Monasteries',
    description_de: `Begeben Sie sich auf eine faszinierende Zeitreise zu den ältesten christlichen Klöstern der Welt bei diesem exklusiven privaten Tagesausflug ab Hurghada. Gemeinsam mit Ihrem deutschsprachigen Reiseleiter entdecken Sie das berühmte St. Antonius Kloster sowie das St. Paulus Kloster, zwei der bedeutendsten spirituellen und historischen Sehenswürdigkeiten Ägyptens.

Während der Fahrt durch die beeindruckende Wüstenlandschaft des Roten Meeres erfahren Sie spannende Geschichten über die Anfänge des christlichen Mönchtums und das Leben der ersten Einsiedler. Besichtigen Sie jahrhundertealte Kirchen, kunstvolle Wandmalereien, historische Manuskripte und einzigartige Bauwerke, die bis heute von der bewegenden Geschichte der koptischen Kirche erzählen.

Diese private Tour ist ideal für alle, die Kultur, Geschichte und Spiritualität abseits der bekannten Touristenwege erleben möchten. Genießen Sie persönlichen Service, flexible Betreuung und einen unvergesslichen Tag fernab des Massentourismus – exklusiv mit Yallabina Tours.`,
    description_en: `Travel back in time on this exclusive private day trip from Hurghada to the world's oldest Christian monasteries. Together with your English-speaking guide, visit the famous Monastery of St. Anthony and the Monastery of St. Paul, two of Egypt's most important spiritual and historical landmarks.

Drive through the spectacular Eastern Desert while learning about the origins of Christian monastic life and the inspiring stories of the first desert monks. Explore ancient churches, remarkable architecture, beautiful wall paintings, historic manuscripts and sacred places that have preserved their unique atmosphere for centuries.

This private excursion is perfect for travelers interested in history, culture and religion who want to experience authentic Egypt away from the crowds. Enjoy personal service, a flexible itinerary and a memorable cultural journey with Yallabina Tours.`,
    price: 75,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/8686d0161163fe2821c74d96bab397b5.jpeg',
    gallery: [
      '/images/hurghada/hurghada-st-antonius-paulus/1.jpg',
      '/images/hurghada/hurghada-st-antonius-paulus/2.jpg',
      '/images/hurghada/hurghada-st-antonius-paulus/3.jpg',
      '/images/hurghada/hurghada-st-antonius-paulus/4.jpg',
      '/images/hurghada/hurghada-st-antonius-paulus/5.jpg',
      '/images/hurghada/hurghada-st-antonius-paulus/6.jpg',
      '/images/hurghada/hurghada-st-antonius-paulus/7.jpg',
      '/images/hurghada/hurghada-st-antonius-paulus/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 112,
    highlights_de: [
'Privater Tagesausflug zu den St. Antonius & St. Paulus Klöstern',
'Deutschsprachiger Reiseleiter',
'Die ältesten christlichen Klöster Ägyptens',
'Geschichte des frühen Mönchtums',
'Beeindruckende Wüstenlandschaften',
'Historische Kirchen und Manuskripte',
'Mittagessen inklusive',
'Keine Verkaufsveranstaltungen'
],
    highlights_en: [
'Private St. Anthony & St. Paul Monasteries tour',
'English-speaking guide',
"Egypt's oldest Christian monasteries",
'History of early Christian monasticism',
'Spectacular Eastern Desert scenery',
'Historic churches and ancient manuscripts',
'Lunch included',
'No shopping stops'
],
    itinerary_de: [
'07:00 – Abholung von Ihrem Hotel in Hurghada',
'Fahrt durch die Östliche Wüste nach Zafarana',
'Besichtigung des St. Antonius Klosters',
'Mittagessen im Restaurant',
'Besichtigung des St. Paulus Klosters',
'Freie Zeit für Fotos',
'Rückfahrt nach Hurghada',
'Transfer zu Ihrem Hotel'
],
    itinerary_en: [
'07:00 – Hotel pickup in Hurghada',
'Drive through the Eastern Desert to Zafarana',
'Visit St. Anthony Monastery',
'Lunch at a local restaurant',
'Visit St. Paul Monastery',
'Free time for photos',
'Drive back to Hurghada',
'Transfer to your hotel'
],
    included_de: [
'Transfers im klimatisierten Fahrzeug',
'Deutschsprachiger Reiseleiter',
'Besichtigung des St. Antonius Klosters',
'Besichtigung des St. Paulus Klosters',
'Alle Eintrittsgebühren',
'Mittagessen',
'Mineralwasser und Softdrinks während der Fahrt'
],
    included_en: [
'Air-conditioned transfers',
'English-speaking guide',
'Visit to St. Anthony Monastery',
'Visit to St. Paul Monastery',
'All entrance fees',
'Lunch',
'Mineral water & soft drinks during the drive'
],
    excluded_de:  [
'Getränke im Restaurant',
'Persönliche Ausgaben',
'Trinkgelder'
],
    excluded_en: [
'Drinks at the restaurant',
'Personal expenses',
'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 95 },
      { persons: '3 Pers.', price: 90 },
      { persons: '4 Pers.', price: 85 },
      { persons: '5–6 Pers.', price: 80 },
      { persons: '7–8 Pers.', price: 75 },
    ],
    child_price: 40,
  },
  {
    id: 'hurghada-private-boot',
    departure_city: 'hurghada',
    title_de: 'Private Bootstour',
    title_en: 'Private Boat Tour',
    description_de: 'Erleben Sie einen exklusiven Tag auf dem Roten Meer mit einer privaten Bootstour ab Hurghada von Yallabina Tours. Genießen Sie absolute Privatsphäre an Bord Ihres eigenen Bootes und entdecken Sie die schönsten Schnorchelplätze mit farbenfrohen Korallenriffen und einer faszinierenden Unterwasserwelt. Während der Tour erwarten Sie mehrere geführte Schnorchelstopps, ausreichend Zeit zum Schwimmen und Entspannen sowie ein frisch zubereitetes Frühstück und Mittagessen an Bord. Warme und kalte Getränke sind während des gesamten Tages inklusive. Ihr deutschsprachiger Reiseleiter begleitet Sie persönlich vom Hotel bis zur Rückkehr. Komfortable Transfers im klimatisierten Fahrzeug, individueller Service und keine Verkaufsveranstaltungen sorgen für ein entspanntes und unvergessliches Erlebnis auf dem Roten Meer.',
    description_en: 'Enjoy an exclusive day on the Red Sea with a private boat trip from Hurghada by Yallabina Tours. Experience complete privacy aboard your own boat while exploring beautiful snorkeling spots with colorful coral reefs and crystal-clear waters. Spend the day swimming, snorkeling and relaxing far away from crowded tourist boats. Several guided snorkeling stops, freshly prepared breakfast and lunch on board, as well as hot and cold drinks are included throughout the trip. Your English-speaking guide will accompany you from hotel pickup until your return. Comfortable air-conditioned transfers, personalized service and no shopping stops ensure a relaxing and unforgettable experience on the Red Sea.',
    price: 90,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/98dfb75e187a8000adf8bf648cc7ca17.jpeg',
    gallery: [
      '/images/hurghada/hurghada-private-boot/1.jpg',
      '/images/hurghada/hurghada-private-boot/2.jpg',
      '/images/hurghada/hurghada-private-boot/3.jpg',
      '/images/hurghada/hurghada-private-boot/4.jpg',
      '/images/hurghada/hurghada-private-boot/5.jpg',
      '/images/hurghada/hurghada-private-boot/6.jpg',
      '/images/hurghada/hurghada-private-boot/7.jpg',
      '/images/hurghada/hurghada-private-boot/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 89,
    highlights_de: [
'Private Bootstour nur für Ihre Gruppe',
'Deutschsprachiger Reiseleiter',
'Mehrere Schnorchelstopps',
'Farbenfrohe Korallenriffe',
'Frühstück & Mittagessen an Bord',
'Warme und kalte Getränke inklusive',
'Freie Zeit zum Schwimmen & Entspannen',
'Keine Verkaufsveranstaltungen'
],
    highlights_en:  [
'Private boat exclusively for your group',
'English-speaking guide',
'Multiple snorkeling stops',
'Colorful coral reefs',
'Breakfast & lunch on board',
'Hot & cold drinks included',
'Free time for swimming & relaxation',
'No shopping stops'
],
    itinerary_de: [
'09:00 – Abholung von Ihrem Hotel',
'Transfer zur Marina',
'Begrüßung an Bord des Privatbootes',
'Mehrere Schnorchelstopps an wunderschönen Korallenriffen',
'Frühstück und Mittagessen an Bord',
'Freie Zeit zum Schwimmen und Entspannen',
'Rückfahrt zur Marina',
'Transfer zurück zum Hotel'
],
    itinerary_en: [
'09:00 – Hotel pickup',
'Transfer to the marina',
'Welcome aboard your private boat',
'Multiple snorkeling stops at beautiful coral reefs',
'Breakfast and lunch on board',
'Free time for swimming and relaxation',
'Return to the marina',
'Transfer back to your hotel'
],
    included_de: [
'Private Bootstour',
'Deutschsprachiger Reiseleiter',
'Schnorchelausrüstung',
'Schnorchelguide',
'Mehrere Schnorchelstopps',
'Schwimmwesten',
'Mittagessen an Bord',
'Warme und kalte Getränke an Bord',
'Toiletten & Süßwasserduschen',
'Privater Transfer vom und zum Hotel',
'Alle Steuern und Servicegebühren'
],
    included_en: [
'Private boat',
'English-speaking guide',
'Snorkeling equipment',
'Professional snorkeling guide',
'Multiple snorkeling stops',
'Life jackets',
'Lunch on board',
'Hot & cold drinks on board',
'Toilets & freshwater showers',
'Private hotel transfers',
'All taxes and service charges'
],
    excluded_de: [
'Persönliche Ausgaben',
'Trinkgelder',
'Alle Leistungen, die nicht im Reiseprogramm erwähnt sind'
],
    excluded_en: [
'Personal expenses',
'Tips',
'Any services not mentioned in the tour program'
],
    meeting_point_de: 'Abholung von Ihrem Hotel in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 175 },
      { persons: '3 Pers.', price: 150 },
      { persons: '4 Pers.', price: 130 },
      { persons: '5–6 Pers.', price: 100 },
      { persons: '7–8 Pers.', price: 90 },
    ],
    child_price: 50,
  },
  {
    id: 'hurghada-sharm-el-naga',
    departure_city: 'hurghada',
    title_de: 'Sharm El Naga',
    title_en: 'Sharm El Naga',
    description_de: `Erleben Sie einen entspannten Schnorchelausflug zum wunderschönen Naturschutzgebiet Sharm El Naga südlich von Hurghada mit Yallabina Tours. Das geschützte Hausriff beginnt direkt am Strand und zählt zu den schönsten Schnorchelplätzen am Roten Meer.

Entdecken Sie farbenfrohe Korallenriffe und eine faszinierende Unterwasserwelt mit unzähligen tropischen Fischen – ganz ohne Bootsfahrt. Genießen Sie einen entspannten Tag am feinen Sandstrand mit ausreichend Zeit zum Schwimmen, Schnorcheln oder Sonnenbaden.

Ein leckeres Mittagessen ist inklusive. Während der gesamten Tour begleitet Sie unser deutschsprachiger Reiseleiter. Selbstverständlich reisen Sie komfortabel im privaten, klimatisierten Transfer von und zu Ihrem Hotel – ohne Verkaufsveranstaltungen und ohne Zeitdruck.`,
    description_en: `Enjoy a relaxing snorkeling trip to the beautiful Sharm El Naga Nature Reserve south of Hurghada with Yallabina Tours. The protected house reef begins directly from the beach and is considered one of the best snorkeling spots in the Red Sea.

Discover colorful coral reefs, crystal-clear waters and an amazing underwater world filled with tropical fish – all without the need for a boat trip. Spend a peaceful day swimming, snorkeling or simply relaxing on the sandy beach.

A delicious lunch is included. Throughout the entire excursion, you will be accompanied by your English-speaking guide. Travel comfortably with private air-conditioned hotel transfers and enjoy a stress-free experience with no shopping stops or unnecessary delays.`,
    price: 55,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/57d353ce9d4cf70a3af8eda1186b053d.jpeg',
    gallery: [
      '/images/hurghada/hurghada-sharm-el-naga/1.jpg',
      '/images/hurghada/hurghada-sharm-el-naga/2.jpg',
      '/images/hurghada/hurghada-sharm-el-naga/3.jpg',
      '/images/hurghada/hurghada-sharm-el-naga/4.jpg',
      '/images/hurghada/hurghada-sharm-el-naga/5.jpg',
      '/images/hurghada/hurghada-sharm-el-naga/6.jpg',
      '/images/hurghada/hurghada-sharm-el-naga/7.jpg',
      '/images/hurghada/hurghada-sharm-el-naga/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 267,
    highlights_de: ['Privater Schnorchelausflug',
'Deutschsprachiger Reiseleiter',
'Hausriff direkt am Strand',
'Kristallklares Wasser',
'Farbenfrohe Korallenriffe',
'Tropische Fische',
'Mittagessen inklusive',
'Keine Verkaufsveranstaltungen'],
    highlights_en: ['Private snorkeling trip',
'English-speaking guide',
'House reef directly from the beach',
'Crystal-clear water',
'Colorful coral reefs',
'Tropical fish',
'Lunch included',
'No shopping stops'],
    itinerary_de: ['08:00 – Abholung von Ihrem Hotel in Hurghada',
'Fahrt zum Naturschutzgebiet Sharm El Naga',
'Freie Zeit zum Schnorcheln am Hausriff',
'Schwimmen und Entspannen am Sandstrand',
'Mittagessen im Strandrestaurant',
'Freie Zeit zum Sonnenbaden oder Schnorcheln',
'Rückfahrt zu Ihrem Hotel'],
    itinerary_en: ['08:00 – Hotel pickup in Hurghada',
'Drive to Sharm El Naga Nature Reserve',
'Free time for snorkeling at the house reef',
'Swimming and relaxing on the sandy beach',
'Lunch at the beach restaurant',
'Free time for sunbathing or snorkeling',
'Return to your hotel'],
    included_de: ['Privater Transfer vom und zum Hotel',
'Eintritt in das Naturschutzgebiet',
'Deutschsprachiger Reiseleiter',
'Mittagessen',
'Softdrinks und Mineralwasser',
'Freie Nutzung des Strandes',
'Alle Steuern und Servicegebühren'],
    included_en: ['Private hotel transfers',
'Entrance to Sharm El Naga Nature Reserve',
'English-speaking guide',
'Lunch',
'Soft drinks and mineral water',
'Free beach access',
'All taxes and service charges'],
    excluded_de: ['Persönliche Ausgaben',
'Trinkgelder',
'Alle Leistungen, die nicht im Reiseprogramm erwähnt sind'],
    excluded_en: ['Personal expenses',
'Tips',
'Any services not mentioned in the tour program'],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 55 }],
    child_price: 25,
  },
  {
    id: 'hurghada-abu-dabbab',
    departure_city: 'hurghada',
    title_de: 'Abu Dabbab',
    title_en: 'Abu Dabbab',
    description_de: `
Erleben Sie einen unvergesslichen Schnorchelausflug zur weltberühmten Abu Dabbab Bucht mit Yallabina Tours. Dieses einzigartige Naturschutzgebiet zählt zu den besten Schnorchelplätzen am Roten Meer und ist bekannt für seine frei lebenden Meeresschildkröten sowie die seltenen Dugongs (Seekühe).

Schnorcheln Sie über farbenfrohen Korallenriffen, entdecken Sie die faszinierende Unterwasserwelt und entspannen Sie anschließend am feinen Sandstrand. Mit etwas Glück begegnen Sie den berühmten Meeresschildkröten und beobachten zahlreiche tropische Fischarten in kristallklarem Wasser.

Während des gesamten Tages begleitet Sie Ihr deutschsprachiger Reiseleiter. Ein leckeres Mittagessen ist inklusive. Komfortable Transfers im klimatisierten Fahrzeug, persönliche Betreuung und keine Verkaufsveranstaltungen sorgen für einen entspannten und unvergesslichen Tag am Roten Meer.
`,

description_en: `
Experience an unforgettable snorkeling trip to the world-famous Abu Dabbab Bay with Yallabina Tours. This protected marine reserve is one of the best snorkeling destinations in the Red Sea and is famous for its resident sea turtles and the rare dugongs.

Snorkel above colorful coral reefs, discover an incredible underwater world and relax on the beautiful sandy beach. With a little luck, you will swim alongside sea turtles and observe a wide variety of tropical fish in crystal-clear water.

Your English-speaking guide will accompany you throughout the excursion. A delicious lunch is included, along with comfortable air-conditioned hotel transfers. Enjoy a relaxed day with no shopping stops—just nature, snorkeling and unforgettable memories.
`,
    price: 100,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/7051ffda8fd714c7d7baeb543b2c1946.jpeg',
    gallery: [
      '/images/hurghada/hurghada-abu-dabbab/1.jpg',
      '/images/hurghada/hurghada-abu-dabbab/2.jpg',
      '/images/hurghada/hurghada-abu-dabbab/3.jpg',
      '/images/hurghada/hurghada-abu-dabbab/4.jpg',
      '/images/hurghada/hurghada-abu-dabbab/5.jpg',
      '/images/hurghada/hurghada-abu-dabbab/6.jpg',
      '/images/hurghada/hurghada-abu-dabbab/7.jpg',
      '/images/hurghada/hurghada-abu-dabbab/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 367,
    highlights_de: [
'Meeresschildkröten in freier Natur',
'Dugongs (Seekühe) mit etwas Glück',
'Kristallklares Wasser',
'Eines der besten Schnorchelgebiete am Roten Meer',
'Deutschsprachiger Reiseleiter',
'Sandstrand mit Sonnenschirmen',
'Mittagessen inklusive',
'Keine Verkaufsveranstaltungen'
],
    highlights_en: [
'Sea turtles in their natural habitat',
'Chance to see dugongs',
'Crystal-clear water',
'One of the best snorkeling spots in the Red Sea',
'English-speaking guide',
'Sandy beach with sun umbrellas',
'Lunch included',
'No shopping stops'
],
    itinerary_de: [
' Abholung von Ihrem Hotel in Hurghada',
'Fahrt zur Abu Dabbab Bucht',
'Schnorcheln mit Meeresschildkröten',
'Freie Zeit zum Schwimmen und Entspannen am Strand',
'Mittagessen',
'Zweiter Schnorchelgang',
'Rückfahrt zum Hotel'
],
    itinerary_en: [
' Hotel pickup in Hurghada',
'Drive to Abu Dabbab Bay',
'Snorkeling with sea turtles',
'Free time to swim and relax on the beach',
'Lunch',
'Second snorkeling session',
'Return to your hotel'
],
    included_de: [
'Privater Transfer vom und zum Hotel',
'Deutschsprachiger Reiseleiter',
'Schnorchelausrüstung',
'Eintritt nach Abu Dabbab',
'Zwei Schnorchelgänge',
'Sonnenschirme',
'Toiletten & Süßwasserduschen',
'Mittagessen',
'Alle Steuern und Servicegebühren'
],
    included_en: [
'Private hotel transfers',
'English-speaking guide',
'Snorkeling equipment',
'Entrance to Abu Dabbab',
'Two snorkeling sessions',
'Sun umbrellas',
'Toilets & freshwater showers',
'Lunch',
'All taxes and service charges'
],
    excluded_de: [
'Persönliche Ausgaben',
'Trinkgelder',
'Alle Leistungen, die nicht im Reiseprogramm erwähnt sind'
],
    excluded_en: [
'Personal expenses',
'Tips',
'Any services not mentioned in the tour program'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 130 },
      { persons: '3 Pers.', price: 125 },
      { persons: '4 Pers.', price: 120 },
      { persons: '5–6 Pers.', price: 110 },
      { persons: '7–8 Pers.', price: 100 },
    ],
    child_price: 50,
  },
  {
    id: 'hurghada-paradise-island',
    departure_city: 'hurghada',
    title_de: 'Paradise Insel Bootsfahrt',
    title_en: 'Paradise Island Boat Trip',
    description_de: ` Erleben Sie einen unvergesslichen Tagesausflug zur Paradise Island mit Yallabina Tours. Freuen Sie sich auf traumhafte weiße Sandstrände, kristallklares türkisfarbenes Wasser und eine entspannte Atmosphäre fernab vom Massentourismus – der perfekte Ort zum Erholen und Genießen. Während der Bootsfahrt erwarten Sie zwei Schnorchelstopps an beeindruckenden Korallenriffen. Entdecken Sie die faszinierende Unterwasserwelt des Roten Meeres mit bunten Korallen und zahlreichen tropischen Fischarten. Auf Paradise Island haben Sie anschließend ausreichend Zeit zum Schwimmen, Sonnenbaden oder Entspannen unter den Sonnenschirmen am feinen Sandstrand. Ihr deutschsprachiger Reiseleiter begleitet Sie während des gesamten Ausflugs. Ein leckeres Mittagessen sowie warme und kalte Getränke sind inklusive. Komfortable Hoteltransfers im klimatisierten Fahrzeug, professionelle Organisation und keine Verkaufsveranstaltungen garantieren Ihnen einen rundum entspannten Urlaubstag. `,
    description_en: ` Spend an unforgettable day on Paradise Island with Yallabina Tours. Relax on breathtaking white sandy beaches, swim in crystal-clear turquoise water and enjoy the peaceful atmosphere away from the crowds—an ideal escape in the Red Sea. During the boat trip, you will enjoy two snorkeling stops at beautiful coral reefs where you can discover colorful corals, tropical fish and the amazing underwater world of the Red Sea. Once on Paradise Island, you'll have plenty of free time to swim, sunbathe or simply relax under the sun umbrellas on the soft sandy beach. Your English-speaking guide will accompany you throughout the excursion. A delicious lunch, as well as hot and cold drinks, are included. Comfortable air-conditioned hotel transfers, professional service and no shopping stops ensure a relaxing and unforgettable day in paradise. `,
    price: 50,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/15da50eef49cf8cab175b24af870289f.jpeg',
    gallery: [
      '/images/hurghada/hurghada-paradise-island/1.jpg',
      '/images/hurghada/hurghada-paradise-island/2.jpg',
      '/images/hurghada/hurghada-paradise-island/3.jpg',
      '/images/hurghada/hurghada-paradise-island/4.jpg',
      '/images/hurghada/hurghada-paradise-island/5.jpg',
      '/images/hurghada/hurghada-paradise-island/6.jpg',
      '/images/hurghada/hurghada-paradise-island/7.jpg',
      '/images/hurghada/hurghada-paradise-island/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 423,
    highlights_de: [
'Paradiesische Sandstrände',
'Türkisfarbenes Wasser',
'Zwei Schnorchelstopps',
'Deutschsprachiger Reiseleiter',
'Korallenriffe & tropische Fische',
'Mittagessen inklusive',
'Warme & kalte Getränke',
],
    highlights_en: [
'Paradise white-sand beaches',
'Crystal-clear turquoise water',
'Two snorkeling stops',
'English-speaking guide',
'Coral reefs & tropical fish',
'Lunch included',
'Hot & cold drinks',
],
    itinerary_de: [
'09:00 – Abholung von Ihrem Hotel in Hurghada',
'Transfer zum Hafen',
'Bootsfahrt zur Paradise Island',
'Erster Schnorchelstopp',
'Freie Zeit auf Paradise Island',
'Mittagessen',
'Zweiter Schnorchelstopp',
'Rückfahrt zum Hotel'
],
    itinerary_en: [
'09:00 – Hotel pickup in Hurghada',
'Transfer to the marina',
'Boat trip to Paradise Island',
'First snorkeling stop',
'Free time on Paradise Island',
'Lunch',
'Second snorkeling stop',
'Return to your hotel'
],
    included_de: [
'Privater Transfer vom und zum Hotel',
'Deutschsprachiger Reiseleiter',
'Bootsfahrt',
'Eintritt zur Paradise Island',
'Schnorchelausrüstung',
'Zwei Schnorchelstopps',
'Schwimmwesten',
'Sonnenschirme & Liegen',
'Toiletten & Süßwasserduschen',
'Mittagessen',
'Warme & kalte Getränke',
'Alle Steuern und Servicegebühren'
],
    included_en: [
'Private hotel transfers',
'English-speaking guide',
'Boat trip',
'Paradise Island entrance fee',
'Snorkeling equipment',
'Two snorkeling stops',
'Life jackets',
'Sun umbrellas & sunbeds',
'Toilets & freshwater showers',
'Lunch',
'Hot & cold drinks',
'All taxes and service charges'
],
    excluded_de: [
'Persönliche Ausgaben',
'Trinkgelder',
'Alle Leistungen, die nicht im Reiseprogramm erwähnt sind'
],
    excluded_en: [
'Personal expenses',
'Tips',
'Any services not mentioned in the tour program'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 40 }],
    child_price: 20,
  },
  {
    id: 'hurghada-orange-bay',
    departure_city: 'hurghada',
    title_de: 'Orange Bay Insel Tour',
    title_en: 'Orange Bay Island Tour',
    description_de: `
Erleben Sie einen traumhaften Tagesausflug zur Orange Bay mit Yallabina Tours. Genießen Sie karibisches Flair mit puderweißem Sand, kristallklarem türkisfarbenem Wasser und einer entspannten Atmosphäre fernab des Massentourismus.

Während der Bootsfahrt legen wir mehrere Schnorchelstopps an farbenfrohen Korallenriffen ein. Entdecken Sie die faszinierende Unterwasserwelt des Roten Meeres mit bunten tropischen Fischen und beeindruckenden Korallenformationen.

Auf der Orange Bay verbringen Sie entspannte Stunden am wunderschönen Sandstrand. Schwimmen, schnorcheln oder relaxen Sie unter Sonnenschirmen auf bequemen Liegen. Ein leckeres Mittagessen sowie warme und kalte Getränke an Bord sind inklusive. Ihr deutschsprachiger Reiseleiter begleitet Sie den gesamten Tag. Komfortable Hoteltransfers im klimatisierten Fahrzeug sorgen für einen rundum gelungenen Ausflug.
`,
    description_en: `
Experience an unforgettable day trip to Orange Bay with Yallabina Tours. Enjoy Caribbean-style white sandy beaches, crystal-clear turquoise water and a peaceful atmosphere away from the crowded tourist areas.

During the boat trip, you will stop several times at beautiful coral reefs for snorkeling. Discover the fascinating underwater world of the Red Sea with colorful tropical fish and vibrant coral gardens.

Spend relaxing hours on Orange Bay swimming, snorkeling or simply enjoying the sunshine on comfortable beach loungers under parasols. A delicious lunch and hot & cold drinks are included on board. Your English-speaking guide will accompany you throughout the excursion, while comfortable air-conditioned hotel transfers ensure a relaxing and unforgettable day.
`,
    price: 50,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/ee0c94c534935ea8aa49e1edcd4a184e.jpeg',
    gallery: [
      '/images/hurghada/hurghada-orange-bay/1.jpg',
      '/images/hurghada/hurghada-orange-bay/2.jpg',
      '/images/hurghada/hurghada-orange-bay/3.jpg',
      '/images/hurghada/hurghada-orange-bay/4.jpg',
      '/images/hurghada/hurghada-orange-bay/5.jpg',
      '/images/hurghada/hurghada-orange-bay/6.jpg',
      '/images/hurghada/hurghada-orange-bay/7.jpg',
      '/images/hurghada/hurghada-orange-bay/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 534,
    highlights_de: [
  'Karibischer Sandstrand',
  'Kristallklares türkisfarbenes Wasser',
  'Mehrere Schnorchelstopps',
  'Mittagessen und Getränke an Bord',
  'Entspannung auf Orange Bay'
],
    highlights_en: [
  'Caribbean white beach',
  'Crystal-clear turquoise water',
  'Multiple snorkeling stops',
  'Lunch & drinks on board',
  'Relaxing Orange Bay experience'
],
    itinerary_de: [
  '08:00 – Hotelabholung',
  'Bootsfahrt zur Orange Bay',
  'Mehrere Schnorchelstopps',
  'Freizeit auf Orange Bay',
  'Mittagessen an Bord',
  '16:00 – Rückkehr'
],
    itinerary_en: [
  '08:00 – Hotel pickup',
  'Boat trip to Orange Bay',
  'Multiple snorkeling stops',
  'Free time on Orange Bay',
  'Lunch on board',
  '16:00 – Return'
],
    included_de: [
  'Deutschsprachiger Reiseleiter',
  'Eintritt zur Orange Bay',
  'Schnorchelausrüstung',
  'Schnorchelguide',
  'Mehrere Schnorchelstopps',
  'Schwimmwesten',
  'Toiletten & Süßwasserduschen',
  'Sonnenschirme & Liegen',
  'Privater Transfer vom und zum Hotel',
  'Mittagessen an Bord',
  'Warme und kalte Getränke',
  'Alle Steuern und Servicegebühren'
],
    included_en: [
  'English-speaking guide',
  'Orange Bay entrance fee',
  'Snorkeling equipment',
  'Professional snorkeling guide',
  'Multiple snorkeling stops',
  'Life jackets',
  'Toilets & freshwater showers',
  'Sun umbrellas & loungers',
  'Private hotel transfers',
  'Lunch on board',
  'Hot & cold drinks',
  'All taxes and service charges'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder',
  'Alle Leistungen, die nicht im Reiseprogramm erwähnt sind'
],
    excluded_en: [
  'Personal expenses',
  'Tips',
  'Any services not mentioned in the tour program'
],
   meeting_point_de: 'Hotelabholung in Hurghada',
meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 40 }],
    child_price: 20,
  },
  {
    id: 'hurghada-dolphin-speedboat',
    departure_city: 'hurghada',
    title_de: 'Delfin Tour mit Speedboot',
    title_en: 'Dolphin Tour by Speedboat',
    description_de: `
Erleben Sie eine aufregende Speedboot-Tour mit Yallabina Tours und entdecken Sie die faszinierende Unterwasserwelt des Roten Meeres. Mit etwas Glück begegnen Sie frei lebenden Delfinen in ihrem natürlichen Lebensraum – ein unvergessliches Erlebnis für die ganze Familie.

Während der Tour besuchen wir mehrere wunderschöne Schnorchelplätze mit farbenfrohen Korallenriffen und kristallklarem Wasser. Die komplette Schnorchelausrüstung steht Ihnen selbstverständlich zur Verfügung. Zwischendurch legen wir einen Stopp an einem traumhaften weißen Sandstrand ein, wo Sie schwimmen, entspannen und die einzigartige Atmosphäre des Roten Meeres genießen können.

Ihr deutschsprachiger Reiseleiter begleitet Sie während der gesamten Tour. Softdrinks und frisches Obst an Bord sind inklusive. Komfortable Hoteltransfers im klimatisierten Fahrzeug sowie alle Nationalpark- und Umweltgebühren sorgen für einen sorgenfreien und unvergesslichen Ausflug.
`,
    description_en: `
Experience an exciting speedboat adventure with Yallabina Tours and discover the spectacular underwater world of the Red Sea. With a little luck, you may encounter wild dolphins in their natural habitat—an unforgettable experience for couples, families and friends.

During the trip, we visit several beautiful snorkeling spots with colorful coral reefs and crystal-clear water. Complete snorkeling equipment is provided, allowing you to fully enjoy the incredible marine life. You will also stop at a stunning white sandy beach where you can swim, relax and soak up the breathtaking scenery.

Your English-speaking guide will accompany you throughout the excursion. Soft drinks and fresh fruit are included on board. Comfortable air-conditioned hotel transfers, together with all national park and environmental fees, are included for a relaxing and worry-free experience.
`,
    price: 45,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/adf60f75273253d7cbb659b6609d07d6.jpeg',
    gallery: [
      '/images/hurghada/hurghada-dolphin-speedboat/1.jpg',
      '/images/hurghada/hurghada-dolphin-speedboat/2.jpg',
      '/images/hurghada/hurghada-dolphin-speedboat/3.jpg',
      '/images/hurghada/hurghada-dolphin-speedboat/4.jpg',
      '/images/hurghada/hurghada-dolphin-speedboat/5.jpg',
      '/images/hurghada/hurghada-dolphin-speedboat/6.jpg',
      '/images/hurghada/hurghada-dolphin-speedboat/7.jpg',
      '/images/hurghada/hurghada-dolphin-speedboat/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 312,
    highlights_de: [
  'Speedboot-Abenteuer',
  'Schwimmen mit wilden Delfinen',
  'Mehrere Schnorchelstopps',
  'Weißer Sandstrand',
  'Softdrinks & frisches Obst'
],
    highlights_en: [
  'Speedboat adventure',
  'Swimming with wild dolphins',
  'Multiple snorkeling stops',
  'White sandy beach',
  'Soft drinks & fresh fruit'
],
    itinerary_de: [
  '08:00 – Hotelabholung',
  'Speedboot-Abfahrt',
  'Delfinsuche im offenen Meer',
  'Mehrere Schnorchelstopps',
  'Aufenthalt am weißen Sandstrand',
  '12:00 – Rückkehr'
],
    itinerary_en: [
  '08:00 – Hotel pickup',
  'Speedboat departure',
  'Dolphin search in the open sea',
  'Multiple snorkeling stops',
  'Relaxing stop at a white sandy beach',
  '12:00 – Return'
],
    included_de: [
  'Speedboot',
  'Deutschsprachiger Reiseleiter',
  'Komplette Schnorchelausrüstung',
  'Schwimmwesten',
  'Privater Transfer vom und zum Hotel',
  'Softdrinks',
  'Frisches Obst',
  'Nationalpark- und Umweltgebühren'
],
    included_en: [
  'Speedboat',
  'English-speaking guide',
  'Complete snorkeling equipment',
  'Life jackets',
  'Private hotel transfers',
  'Soft drinks',
  'Fresh fruit',
  'National park & environmental fees'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder',
  'Neoprenanzug'
],
    excluded_en: [
  'Personal expenses',
  'Tips',
  'Wetsuit'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 89 },
      { persons: '3 Pers.', price: 59 },
      { persons: '4 Pers.', price: 54 },
      { persons: '5–6 Pers.', price: 50 },
      { persons: '7–8 Pers.', price: 45 },
    ],
    child_price: 25,
  },
  {
    id: 'hurghada-quad-private',
    departure_city: 'hurghada',
    title_de: 'Privater Quad Tour Hurghada',
    title_en: 'Private Quad Tour Hurghada',
    description_de: `
Erleben Sie ein exklusives Wüstenabenteuer mit Yallabina Tours und entdecken Sie die beeindruckenden Landschaften der ägyptischen Wüste auf einer privaten Quad-Tour. Genießen Sie rund drei Stunden reine Fahrzeit durch endlose Sanddünen und erleben Sie Freiheit, Adrenalin und unvergessliche Ausblicke fernab des Massentourismus.

Während der Tour besuchen Sie ein traditionelles Beduinendorf, genießen authentischen Beduinentee und erleben einen Kamelritt durch die faszinierende Wüstenlandschaft. Kostenlose Kopftücher und Schutzbrillen sorgen für zusätzlichen Komfort während Ihres Abenteuers.

Ihr deutschsprachiger Reiseleiter begleitet Sie während der gesamten Tour. Private Hoteltransfers im klimatisierten Fahrzeug sowie Wasser und Softdrinks sind inklusive. Freuen Sie sich auf eine perfekte Mischung aus Action, Natur und traditioneller Beduinenkultur.
`,
    description_en: `
Experience an exclusive desert adventure with Yallabina Tours and explore the breathtaking Egyptian desert on a private quad bike tour. Enjoy around three hours of pure riding time across endless sand dunes while experiencing freedom, adrenaline and spectacular desert landscapes far away from the crowded tourist areas.

During the excursion, you will visit a traditional Bedouin village, enjoy authentic Bedouin tea and experience a camel ride through the beautiful desert scenery. Free headscarves and protective goggles are provided to ensure maximum comfort throughout your adventure.

Your English-speaking guide will accompany you during the entire tour. Private air-conditioned hotel transfers, bottled water and soft drinks are included. Enjoy the perfect combination of adventure, nature and authentic Bedouin culture.
`,
    price: 50,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/f88381905ddec27f8d88c0cb7bd07908.jpeg',
    gallery: [
      '/images/hurghada/hurghada-quad-private/1.jpg',
      '/images/hurghada/hurghada-quad-private/2.jpg',
      '/images/hurghada/hurghada-quad-private/3.jpg',
      '/images/hurghada/hurghada-quad-private/4.jpg',
      '/images/hurghada/hurghada-quad-private/5.jpg',
      '/images/hurghada/hurghada-quad-private/6.jpg',
      '/images/hurghada/hurghada-quad-private/7.jpg',
      '/images/hurghada/hurghada-quad-private/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 278,
    highlights_de: [
  '3 Stunden Quadfahren',
  'Privates Wüstenerlebnis',
  'Besuch eines Beduinendorfs',
  'Kamelritt inklusive',
  'Wasser & Softdrinks'
],
    highlights_en: [
  '3-hour quad adventure',
  'Private desert experience',
  'Visit to a Bedouin village',
  'Camel ride included',
  'Water & soft drinks'
],
    itinerary_de: [
  '14:00 – Hotelabholung',
  'Ankunft an der Quadstation',
  'Einweisung & Sicherheitsbriefing',
  '3 Stunden Quadfahren',
  'Besuch des Beduinendorfs',
  'Kamelritt & Beduinentee',
  '19:00 – Rückkehr'
],
    itinerary_en: [
  '14:00 – Hotel pickup',
  'Arrival at the quad station',
  'Safety briefing',
  '3-hour quad ride',
  'Visit to the Bedouin village',
  'Camel ride & Bedouin tea',
  '19:00 – Return'
],
   included_de: [
  'Deutschsprachiger Reiseleiter',
  'Privates Quad',
  '3 Stunden Quadfahren',
  'Besuch eines Beduinendorfs',
  'Kamelritt',
  'Beduinentee',
  'Kopftuch',
  'Schutzbrille',
  'Privater Transfer vom und zum Hotel',
  'Wasser & Softdrinks',
  'Alle Steuern und Servicegebühren'
],
    included_en: [
  'English-speaking guide',
  'Private quad bike',
  '3-hour quad ride',
  'Visit to a Bedouin village',
  'Camel ride',
  'Bedouin tea',
  'Headscarf',
  'Protective goggles',
  'Private hotel transfers',
  'Water & soft drinks',
  'All taxes and service charges'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 50 }],
    child_price: 25,
  },
  {
    id: 'hurghada-ghost-city-quad',
    departure_city: 'hurghada',
    title_de: 'Privater Ghost City Quad Safari',
    title_en: 'Private Ghost City Quad Safari',
    description_de: `
Erleben Sie eine exklusive private Quad-Safari mit Yallabina Tours und entdecken Sie die geheimnisvolle Ghost City bei Safaga. Fahren Sie drei Stunden lang mit Ihrem Quad durch die beeindruckende Wüstenlandschaft und erkunden Sie die verlassene Bergbaustadt Umm Al Huwaytat – einen der faszinierendsten Lost Places Ägyptens.

Während der Tour besuchen Sie die Ruinen der ehemaligen Phosphatstadt, genießen spektakuläre Ausblicke über die Wüste und legen mehrere Fotostopps an den schönsten Aussichtspunkten ein. Ihr deutschsprachiger Guide erzählt Ihnen spannende Geschichten über die Geschichte der einst lebendigen Siedlung.

Erfrischendes Mineralwasser und Softdrinks stehen während der gesamten Tour bereit. Private Hoteltransfers, professionelle Betreuung und ein unvergessliches Offroad-Abenteuer machen diesen Ausflug zu einem echten Highlight Ihres Hurghada-Urlaubs.
`,
    description_en: `
Experience an exclusive private quad safari with Yallabina Tours and discover the mysterious Ghost City near Safaga. Ride your quad bike for three hours through the breathtaking desert landscape and explore the abandoned mining town of Umm Al Huwaytat—one of Egypt's most fascinating ghost towns.

Visit the ruins of the former phosphate village, enjoy spectacular panoramic desert views and stop at several scenic viewpoints for unforgettable photos. Your English-speaking guide will share the fascinating history of this once-thriving settlement.

Mineral water and soft drinks are provided throughout the tour. Private hotel transfers, professional guidance and an exciting off-road adventure make this one of the most unique desert experiences in Hurghada.
`,
    price: 60,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/03116a0233c26199a041f1e4eda1a94a.jpeg',
    gallery: [
      '/images/hurghada/hurghada-ghost-city-quad/1.jpg',
      '/images/hurghada/hurghada-ghost-city-quad/2.jpg',
      '/images/hurghada/hurghada-ghost-city-quad/3.jpg',
      '/images/hurghada/hurghada-ghost-city-quad/4.jpg',
      '/images/hurghada/hurghada-ghost-city-quad/5.jpg',
      '/images/hurghada/hurghada-ghost-city-quad/6.jpg',
      '/images/hurghada/hurghada-ghost-city-quad/7.jpg',
      '/images/hurghada/hurghada-ghost-city-quad/8.jpg',
    ],
    rating: 4.6,
    reviews_count: 145,
    highlights_de: [
  '3 Stunden Quad-Safari',
  'Ghost City entdecken',
  'Spektakuläre Fotostopps',
  'Private Tour',
  'Mineralwasser & Softdrinks'
],
    highlights_en: [
  '3-hour quad safari',
  'Explore Ghost City',
  'Spectacular photo stops',
  'Private tour',
  'Water & soft drinks'
],
    itinerary_de: [
  'Hotelabholung',
  'Ankunft an der Quadstation',
  'Sicherheitseinweisung',
  '3-stündige Quad-Safari',
  'Besichtigung der Ghost City',
  'Fotostopps',
  'Rückfahrt zum Hotel'
],
    itinerary_en: [
  'Hotel pickup',
  'Arrival at the quad station',
  'Safety briefing',
  '3-hour quad safari',
  'Explore Ghost City',
  'Photo stops',
  'Return to the hotel'
],
    included_de: [
  'Privates Quad',
  'Deutschsprachiger Reiseleiter',
  '3 Stunden Quad-Safari',
  'Hoteltransfer',
  'Mineralwasser',
  'Softdrinks',
  'Alle Steuern und Servicegebühren'
],
    included_en: [
  'Private quad bike',
  'English-speaking guide',
  '3-hour quad safari',
  'Hotel transfers',
  'Mineral water',
  'Soft drinks',
  'All taxes and service charges'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 90 },
      { persons: '3 Pers.', price: 80 },
      { persons: '4 Pers.', price: 70 },
      { persons: '5–6 Pers.', price: 65 },
      { persons: '7–8 Pers.', price: 60 },
    ],
    child_price: 45,
  },
  {
    id: 'hurghada-quad-group',
    departure_city: 'hurghada',
    title_de: 'Quad Tour Hurghada (Gruppe)',
    title_en: 'Quad Tour Hurghada (Group)',
    description_de: `
Erleben Sie ein aufregendes Quad-Abenteuer mit Yallabina Tours und entdecken Sie die beeindruckende Wüstenlandschaft rund um Hurghada gemeinsam mit einer kleinen Gruppe von 10 bis 15 Teilnehmern. Genießen Sie drei Stunden Fahrspaß auf dem Quad und erleben Sie die Freiheit der ägyptischen Wüste.

Während der Tour fahren Sie durch endlose Sanddünen, besuchen ein traditionelles Beduinendorf und genießen einen kurzen Kamelritt. Ihr deutschsprachiger Reiseleiter begleitet die Gruppe während des gesamten Ausflugs und sorgt für ein sicheres und unvergessliches Erlebnis.

Kostenlose Arafat-Tücher, Schutzbrillen, Mineralwasser und Softdrinks sind inklusive. Hoteltransfers im klimatisierten Fahrzeug sowie die komplette Quad-Ausrüstung machen diese Gruppentour zu einem der beliebtesten Wüstenausflüge in Hurghada.
`,
    description_en: `
Join an exciting quad adventure with Yallabina Tours and explore the breathtaking desert landscapes around Hurghada together with a small group of 10 to 15 participants. Enjoy three hours of quad riding and experience the freedom of the Egyptian desert.

Ride across endless sand dunes, visit a traditional Bedouin village and enjoy a short camel ride. Your English-speaking guide will accompany the group throughout the tour to ensure a safe and unforgettable experience.

Free headscarves, safety goggles, mineral water and soft drinks are included. Comfortable hotel transfers and full quad equipment make this group safari one of the most popular desert excursions in Hurghada.
`,
    price: 35,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/4e17e9963d93a669c334194d82789006.jpeg',
    gallery: [
      '/images/hurghada/hurghada-quad-group/1.jpg',
      '/images/hurghada/hurghada-quad-group/2.jpg',
      '/images/hurghada/hurghada-quad-group/3.jpg',
      '/images/hurghada/hurghada-quad-group/4.jpg',
      '/images/hurghada/hurghada-quad-group/5.jpg',
      '/images/hurghada/hurghada-quad-group/6.jpg',
      '/images/hurghada/hurghada-quad-group/7.jpg',
      '/images/hurghada/hurghada-quad-group/8.jpg',
    ],
    rating: 4.5,
    reviews_count: 612,
    highlights_de: [
  '3 Stunden Quad-Safari',
  'Kleine Gruppe (10–15 Personen)',
  'Beduinendorf',
  'Kamelritt',
  'Mineralwasser & Softdrinks'
],
    highlights_en: [
  '3-hour quad safari',
  'Small group (10–15 people)',
  'Bedouin village',
  'Camel ride',
  'Water & soft drinks'
],
    itinerary_de: [
  '14:00 – Hotelabholung',
  'Ankunft an der Quadstation',
  'Sicherheitseinweisung',
  '3 Stunden Quadfahren',
  'Besuch des Beduinendorfs',
  'Kamelritt',
  '19:00 – Rückkehr'
],
    itinerary_en: [
  '14:00 – Hotel pickup',
  'Arrival at the quad station',
  'Safety briefing',
  '3-hour quad ride',
  'Visit to the Bedouin village',
  'Camel ride',
  '19:00 – Return'
],
    included_de: [
  'Deutschsprachiger Reiseleiter',
  'Quad',
  '3 Stunden Quad-Safari',
  'Besuch des Beduinendorfs',
  'Kamelritt',
  'Mineralwasser & Softdrinks',
  'Hoteltransfer'
],
    included_en: [
  'English-speaking guide',
  'Quad bike',
  '3-hour quad safari',
  'Bedouin village visit',
  'Camel ride',
  'Mineral water & soft drinks',
  'Hotel transfers'
],
   excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
   excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 35 }],
    child_price: 20,
  },
  {
    id: 'hurghada-buggy-3h',
    departure_city: 'hurghada',
    title_de: '3 Stunden Buggy Tour',
    title_en: '3 Hour Buggy Tour',
   description_de: `
Erleben Sie eine actionreiche Dune-Buggy-Safari mit Yallabina Tours und entdecken Sie die beeindruckende Wüstenlandschaft rund um Hurghada. Steuern Sie Ihren leistungsstarken Buggy selbst durch endlose Sanddünen und genießen Sie ein unvergessliches Offroad-Abenteuer fernab des Massentourismus.

Nach einer kurzen Sicherheitseinweisung beginnt Ihre Fahrt durch die faszinierende Wüste. Folgen Sie Ihrem deutschsprachigen Guide über spektakuläre Dünen, erleben Sie rasante Offroad-Passagen und legen Sie mehrere Fotostopps an den schönsten Aussichtspunkten ein.

Mineralwasser und Softdrinks stehen während der Tour bereit. Hoteltransfers im klimatisierten Fahrzeug sowie professionelle Betreuung sorgen für ein sicheres und unvergessliches Wüstenerlebnis.
`,
    description_en: `
Experience an exciting dune buggy safari with Yallabina Tours and explore the breathtaking desert landscapes around Hurghada. Drive your own powerful buggy across endless sand dunes and enjoy an unforgettable off-road adventure far away from the crowds.

After a short safety briefing, your adventure begins as you follow your English-speaking guide through the spectacular desert. Ride across impressive dunes, experience thrilling off-road tracks and stop at several panoramic viewpoints for unforgettable photos.

Mineral water and soft drinks are provided throughout the tour. Comfortable hotel transfers and professional guidance ensure a safe, exciting and unforgettable desert experience.
`,
    price: 55,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/5a64384bdda216d2334de6452b3ed9a6.jpeg',
    gallery: [
      '/images/hurghada/hurghada-buggy-3h/1.jpg',
      '/images/hurghada/hurghada-buggy-3h/2.jpg',
      '/images/hurghada/hurghada-buggy-3h/3.jpg',
      '/images/hurghada/hurghada-buggy-3h/4.jpg',
      '/images/hurghada/hurghada-buggy-3h/5.jpg',
      '/images/hurghada/hurghada-buggy-3h/6.jpg',
      '/images/hurghada/hurghada-buggy-3h/7.jpg',
      '/images/hurghada/hurghada-buggy-3h/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 198,
    highlights_de: [
  '3 Stunden Dune-Buggy-Safari',
  'Leistungsstarker Buggy',
  'Spektakuläre Dünen',
  'Fotostopps',
  'Mineralwasser & Softdrinks'
],
    highlights_en: [
  '3-hour dune buggy safari',
  'Powerful dune buggy',
  'Spectacular sand dunes',
  'Photo stops',
  'Water & soft drinks'
],
    itinerary_de: [
  '14:00 – Hotelabholung',
  'Ankunft an der Buggystation',
  'Sicherheitseinweisung',
  '3 Stunden Dune-Buggy-Fahrt',
  'Fotostopps',
  '17:30 – Rückkehr'
],
    itinerary_en: [
  '14:00 – Hotel pickup',
  'Arrival at the buggy station',
  'Safety briefing',
  '3-hour dune buggy ride',
  'Photo stops',
  '17:30 – Return'
],
    included_de: [
  'Deutschsprachiger Reiseleiter',
  'Dune-Buggy',
  'Schutzhelm',
  'Hoteltransfer',
  'Mineralwasser',
  'Softdrinks',
  'Alle Steuern und Servicegebühren'
],
    included_en: [
  'English-speaking guide',
  'Dune buggy',
  'Safety helmet',
  'Hotel transfers',
  'Mineral water',
  'Soft drinks',
  'All taxes and service charges'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 65 },
      { persons: '3 Pers.', price: 55 },
      { persons: '4 Pers.', price: 55 },
      { persons: '5–6 Pers.', price: 60 },
      { persons: '7–8 Pers.', price: 55 },
    ],
    child_price: 30,
  },
  {
    id: 'hurghada-bahariya-white-desert',
    departure_city: 'hurghada',
    title_de: 'Bahariya Oase & Weiße Wüste (3 Tage)',
title_en: 'Bahariya Oasis & White Desert (3 Days)',

    description_de: `
Erleben Sie eine unvergessliche 3-tägige Privatreise mit Yallabina Tours zu den beeindruckendsten Natur- und Kulturhighlights Ägyptens. Besuchen Sie zunächst die weltberühmten Pyramiden von Gizeh und die Sphinx, bevor Sie Ihre Reise in die faszinierende Bahariya-Oase und die spektakuläre Weiße Wüste fortsetzen.

Am zweiten Tag entdecken Sie die Schönheit der Bahariya-Oase, den Crystal Mountain, den Black Mountain und die surrealen Kalksteinformationen der Weißen Wüste. Am Abend genießen Sie ein authentisches Wüstencamp unter dem Sternenhimmel mit traditionellem Abendessen und einer einzigartigen Atmosphäre mitten in der Natur.

Nach dem Frühstück erwartet Sie eine aufregende 4x4-Safari durch die Dünen der Weißen Wüste, bevor Sie die Rückfahrt nach Hurghada antreten. Private Transfers, deutschsprachige Ägyptologen, komfortable Unterkünfte und alle wichtigen Eintrittsgebühren machen diese Reise zu einem einmaligen Erlebnis.
`,
    description_en: `
Experience an unforgettable 3-day private adventure with Yallabina Tours, combining Egypt's greatest historical treasures with its most spectacular desert landscapes. Visit the legendary Pyramids of Giza and the Great Sphinx before continuing your journey to the beautiful Bahariya Oasis and the breathtaking White Desert.

On the second day, explore Bahariya Oasis, Crystal Mountain, Black Mountain and the incredible limestone formations of the White Desert. Spend the evening camping beneath the stars, enjoy a traditional dinner and experience the peaceful atmosphere of one of Egypt's most extraordinary national parks.

After breakfast, enjoy an exciting 4x4 safari across the dunes of the White Desert before returning to Hurghada. Private transportation, English-speaking guides, comfortable accommodation and all major entrance fees are included for a truly unforgettable journey.
`,
    price: 360,
    duration: '3 Tage',
duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/e72bba9620521a5b3b0b25f54794ef6e.jpeg',
    gallery: [
      '/images/hurghada/hurghada-bahariya-white-desert/1.jpg',
      '/images/hurghada/hurghada-bahariya-white-desert/2.jpg',
      '/images/hurghada/hurghada-bahariya-white-desert/3.jpg',
      '/images/hurghada/hurghada-bahariya-white-desert/4.jpg',
      '/images/hurghada/hurghada-bahariya-white-desert/5.jpg',
      '/images/hurghada/hurghada-bahariya-white-desert/6.jpg',
      '/images/hurghada/hurghada-bahariya-white-desert/7.jpg',
      '/images/hurghada/hurghada-bahariya-white-desert/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 67,
    highlights_de: [
  'Pyramiden von Gizeh',
  'Sphinx',
  'Bahariya Oase',
  'Crystal Mountain',
  'Black Mountain',
  'Camping in der Weißen Wüste'
],
    highlights_en: [
  'Pyramids of Giza',
  'Great Sphinx',
  'Bahariya Oasis',
  'Crystal Mountain',
  'Black Mountain',
  'Camping in the White Desert'
],
    itinerary_de: [
  'Tag 1: Fahrt nach Kairo, Pyramiden von Gizeh, Sphinx, Hotelübernachtung',
  'Tag 2: Bahariya Oase, Crystal Mountain, Black Mountain, Weiße Wüste, Camping',
  'Tag 3: 4x4-Safari durch die Weiße Wüste, Rückfahrt nach Hurghada'
],
    itinerary_en: [
  'Day 1: Drive to Cairo, Pyramids of Giza, Great Sphinx, overnight hotel stay',
  'Day 2: Bahariya Oasis, Crystal Mountain, Black Mountain, White Desert, camping',
  'Day 3: 4x4 safari through the White Desert, return to Hurghada'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  'Pyramiden von Gizeh',
  'Sphinx',
  'Taltempel',
  'Bahariya Oase',
  'Crystal Mountain',
  'Black Mountain',
  'Weiße Wüste',
  'Privater 4x4-Jeep',
  'Camping mit Vollpension',
  'Eintrittsgebühren',
  'Transfers',
  'Steuern und Servicegebühren'
],
    included_en: [
  'English-speaking Egyptologist',
  'Pyramids of Giza',
  'Great Sphinx',
  'Valley Temple',
  'Bahariya Oasis',
  'Crystal Mountain',
  'Black Mountain',
  'White Desert',
  'Private 4x4 Jeep',
  'Camping with full board',
  'Entrance fees',
  'Transfers',
  'Taxes and service charges'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
  { persons: '2 Pers.', price: 425 },
  { persons: '3-4 Pers.', price: 399 },
  { persons: '5-6 Pers.', price: 370 }
],
child_price: 225,
  },
  {
    id: 'hurghada-aswan-abu-simbel',
    departure_city: 'hurghada',
    title_de: '2-Tagesausflug nach Assuan & Abu Simbel',
    title_en: 'Aswan & Abu Simbel 2-Day Tour',
    description_de: `
Erleben Sie mit Yallabina Tours eine unvergessliche 2-tägige Privatreise nach Assuan und Abu Simbel. Entdecken Sie einige der beeindruckendsten Monumente des alten Ägyptens – vom wunderschönen Philae-Tempel über den gewaltigen Assuan-Staudamm bis zu den weltberühmten Felsentempeln von Ramses II. in Abu Simbel.

Am ersten Tag besuchen Sie den Philae-Tempel, den unvollendeten Obelisken sowie den Assuan-Staudamm, bevor Sie den Abend entspannt in einem komfortablen 4-Sterne-Hotel verbringen. Optional können Sie die faszinierende Sound & Light Show am Philae-Tempel erleben.

Am zweiten Tag fahren Sie früh am Morgen nach Abu Simbel und besichtigen die monumentalen Tempel von Ramses II. und Nefertari. Nach einem Mittagessen in Assuan treten Sie mit vielen unvergesslichen Eindrücken die Rückreise nach Hurghada an.
`,
    description_en: `
Experience an unforgettable 2-day private journey to Aswan and Abu Simbel with Yallabina Tours. Discover some of Egypt's greatest ancient treasures, including the beautiful Philae Temple, the impressive Aswan High Dam and the world-famous rock temples of Ramses II at Abu Simbel.

On the first day, visit Philae Temple, the Unfinished Obelisk and the Aswan High Dam before relaxing in a comfortable 4-star hotel. In the evening, you may optionally enjoy the spectacular Sound & Light Show at Philae Temple.

On the second day, travel early to Abu Simbel to explore the magnificent temples of Ramses II and Queen Nefertari. After lunch in Aswan, return comfortably to Hurghada with unforgettable memories of Upper Egypt.
`,
    price: 420,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/ca0144e8c13b8b0d7f6c9c8ed581143e.jpeg',
    gallery: [
      '/images/hurghada/hurghada-aswan-abu-simbel/1.jpg',
      '/images/hurghada/hurghada-aswan-abu-simbel/2.jpg',
      '/images/hurghada/hurghada-aswan-abu-simbel/3.jpg',
      '/images/hurghada/hurghada-aswan-abu-simbel/4.jpg',
      '/images/hurghada/hurghada-aswan-abu-simbel/5.jpg',
      '/images/hurghada/hurghada-aswan-abu-simbel/6.jpg',
      '/images/hurghada/hurghada-aswan-abu-simbel/7.jpg',
      '/images/hurghada/hurghada-aswan-abu-simbel/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 87,
    highlights_de: [
  'Philae-Tempel',
  'Unvollendeter Obelisk',
  'Assuan-Staudamm',
  'Abu-Simbel-Tempel',
  'Übernachtung im 4-Sterne-Hotel'
],
    highlights_en: [
  'Philae Temple',
  'Unfinished Obelisk',
  'Aswan High Dam',
  'Abu Simbel Temples',
  'Overnight stay in a 4-star hotel'
],
    itinerary_de: [
  'Tag 1: Hotelabholung, Assuan, Philae-Tempel, Unvollendeter Obelisk, Assuan-Staudamm, Hotelübernachtung',
  'Tag 2: Abu Simbel, Mittagessen in Assuan, Rückfahrt nach Hurghada'
],
    itinerary_en: [
  'Day 1: Hotel pickup, Aswan, Philae Temple, Unfinished Obelisk, Aswan High Dam, overnight stay',
  'Day 2: Abu Simbel, lunch in Aswan, return to Hurghada'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  'Philae-Tempel',
  'Unvollendeter Obelisk',
  'Assuan-Staudamm',
  'Abu-Simbel-Tempel',
  'Übernachtung im 4-Sterne-Hotel mit Halbpension',
  'Mittagessen an beiden Tagen',
  'Alle Transfers',
  'Eintrittsgebühren',
  'Softgetränke im Fahrzeug'
],
    included_en: [
  'English-speaking Egyptologist',
  'Philae Temple',
  'Unfinished Obelisk',
  'Aswan High Dam',
  'Abu Simbel Temples',
  'Overnight stay in a 4-star hotel with half board',
  'Lunch on both days',
  'All transfers',
  'Entrance fees',
  'Soft drinks during transfers'
],
    excluded_de: [
  'Getränke im Restaurant',
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Drinks in restaurants',
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Hurghada',
    meeting_point_en: 'Hotel pickup in Hurghada',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 470 },
      { persons: '3 Pers.', price: 460 },
      { persons: '4 Pers.', price: 450 },
      { persons: '5–6 Pers.', price: 440 },
      { persons: '7–8 Pers.', price: 420 },
    ],
    child_price: 170,
  },

  // ==================== MARSA ALAM TOURS ====================
  {
    id: 'marsa-cairo-flight-1day',
    departure_city: 'marsa-alam',
    title_de: 'Tagesausflug nach Kairo mit dem Flugzeug',
    title_en: 'Cairo Day Tour by Flight',
    description_de: `
Unternehmen Sie mit Yallabina Tours einen unvergesslichen Tagesausflug von Marsa Alam nach Kairo mit dem Flugzeug. Entdecken Sie die bedeutendsten Sehenswürdigkeiten Ägyptens, darunter die Pyramiden von Gizeh, die Große Sphinx und das beeindruckende Grand Egyptian Museum (GEM), das größte archäologische Museum der Welt.

Genießen Sie eine private Tour mit einem deutschsprachigen Ägyptologen, der Sie den ganzen Tag begleitet. Neben den weltberühmten Monumenten erleben Sie auch das authentische Kairo bei einer Stadtrundfahrt und besuchen den historischen Khan El Khalili Basar sowie die El-Moez-Straße.

Freuen Sie sich auf einen perfekt organisierten Ausflug ohne Verkaufsveranstaltungen, mit komfortablen Transfers, Inlandsflügen und einem leckeren Mittagessen in einem ausgewählten Restaurant.
`,
    description_en: `
Experience an unforgettable private day trip from Marsa Alam to Cairo by plane with Yallabina Tours. Visit Egypt's most iconic landmarks, including the Great Pyramids of Giza, the Great Sphinx and the spectacular Grand Egyptian Museum (GEM), the largest archaeological museum in the world.

Your English-speaking Egyptologist will accompany you throughout the entire day, sharing fascinating stories about ancient Egypt while you also discover modern Cairo, Khan El Khalili Bazaar and the historic El-Moez Street.

Enjoy a perfectly organized excursion with domestic flights, comfortable transfers, no shopping stops and a delicious lunch at a carefully selected local restaurant.
`,
    price: 240,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/f1bd08441a8ca9b541d73fde94ba759d.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-cairo-flight-1day/1.jpg',
      '/images/marsa-alam/marsa-cairo-flight-1day/2.jpg',
      '/images/marsa-alam/marsa-cairo-flight-1day/3.jpg',
      '/images/marsa-alam/marsa-cairo-flight-1day/4.jpg',
      '/images/marsa-alam/marsa-cairo-flight-1day/5.jpg',
      '/images/marsa-alam/marsa-cairo-flight-1day/6.jpg',
      '/images/marsa-alam/marsa-cairo-flight-1day/7.jpg',
      '/images/marsa-alam/marsa-cairo-flight-1day/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 178,
    highlights_de: [
  'Flug Marsa Alam–Kairo–Marsa Alam',
  'Pyramiden von Gizeh',
  'Große Sphinx',
  'Grand Egyptian Museum (GEM)',
  'Khan El Khalili',
  'El-Moez-Straße',
  'Privater deutschsprachiger Ägyptologe'
],
    highlights_en: [
  'Flight Marsa Alam–Cairo–Marsa Alam',
  'Pyramids of Giza',
  'Great Sphinx',
  'Grand Egyptian Museum (GEM)',
  'Khan El Khalili Bazaar',
  'El-Moez Street',
  'Private German-speaking Egyptologist'
],
    itinerary_de: [
  ' Hotelabholung und Transfer zum Flughafen',
  'Inlandsflug nach Kairo',
  'Pyramiden von Gizeh & Große Sphinx',
  'Mittagessen',
  'Grand Egyptian Museum (GEM)',
  'Khan El Khalili & El-Moez-Straße',
  'Rückflug und Transfer zum Hotel'
],
    itinerary_en: [
  ' Hotel pickup and airport transfer',
  'Domestic flight to Cairo',
  'Pyramids of Giza & Great Sphinx',
  'Lunch',
  'Grand Egyptian Museum (GEM)',
  'Khan El Khalili & El-Moez Street',
  'Flight back and hotel transfer'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  '2 Inlandsflüge',
  'Pyramiden von Gizeh',
  'Große Sphinx',
  'Taltempel von Chephren',
  'Grand Egyptian Museum (GEM)',
  'Alle Transfers',
  'Eintrittsgebühren',
  'Mittagessen',
  'Softgetränke'
],
    included_en: [
  'English-speaking Egyptologist',
  '2 domestic flights',
  'Pyramids of Giza',
  'Great Sphinx',
  'Valley Temple',
  'Grand Egyptian Museum (GEM)',
  'All transfers',
  'Entrance fees',
  'Lunch',
  'Soft drinks'
],
    excluded_de: [
  'Getränke im Restaurant',
  'Persönliche Ausgaben',
  'Trinkgelder'
],

excluded_en: [
  'Drinks in restaurants',
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam, Transfer zum Flughafen Hurghada',

meeting_point_en: 'Hotel pickup in Marsa Alam, transfer to Hurghada Airport',
    languages_de: 'Deutsch, Englisch, Arabisch',
    languages_en: 'German, English, Arabic',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 300 },
      { persons: '3 Pers.', price: 280 },
      { persons: '4 Pers.', price: 250 },
      { persons: '5–6 Pers.', price: 250 },
      { persons: '7–8 Pers.', price: 240 },
    ],
    child_price: 200,
  },
  {
    id: 'marsa-cairo-flight-2day',
    departure_city: 'marsa-alam',
    title_de: '2-Tagesausflug nach Kairo mit dem Flugzeug',
    title_en: 'Cairo 2-Day Tour by Flight',
    description_de: `
Erleben Sie mit Yallabina Tours eine unvergessliche private 2-tägige Flugreise von Marsa Alam nach Kairo. Entdecken Sie die berühmtesten Sehenswürdigkeiten Ägyptens, darunter die Pyramiden von Gizeh, die Große Sphinx, Memphis, Sakkara, die Zitadelle von Saladin, die Mohamed-Ali-Moschee sowie das beeindruckende Grand Egyptian Museum (GEM).

Während der zweitägigen Reise begleitet Sie ein deutschsprachiger Ägyptologe exklusiv durch die faszinierende Geschichte des alten und modernen Kairos. Sie übernachten in einem komfortablen Hotel mit Blick auf die Pyramiden und genießen ausreichend Zeit, die Highlights der Hauptstadt in entspannter Atmosphäre zu erleben.

Freuen Sie sich auf eine perfekt organisierte Privattour mit Inlandsflügen, komfortablen Transfers, ohne Verkaufsveranstaltungen sowie Mittagessen an beiden Tagen. Eine ideale Reise für alle, die Kairo intensiv entdecken möchten.
`,
    description_en: `
Experience an unforgettable private 2-day journey from Marsa Alam to Cairo by plane with Yallabina Tours. Visit Egypt's most famous attractions, including the Pyramids of Giza, the Great Sphinx, Memphis, Saqqara, the Citadel of Saladin, the Mohamed Ali Mosque and the spectacular Grand Egyptian Museum (GEM).

Your private English-speaking Egyptologist will accompany you throughout the entire trip, sharing fascinating insights into Egypt's rich history. Enjoy an overnight stay in a comfortable hotel overlooking the Pyramids and discover Cairo at a relaxed pace.

This perfectly organized tour includes domestic flights, comfortable transfers, lunch on both days and no shopping stops, giving you more time to enjoy Egypt's incredible heritage.
`,
    price: 455,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/41a430b1d9c9255d20b9b5e9d31998a6.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-cairo-flight-2day/1.jpg',
      '/images/marsa-alam/marsa-cairo-flight-2day/2.jpg',
      '/images/marsa-alam/marsa-cairo-flight-2day/3.jpg',
      '/images/marsa-alam/marsa-cairo-flight-2day/4.jpg',
      '/images/marsa-alam/marsa-cairo-flight-2day/5.jpg',
      '/images/marsa-alam/marsa-cairo-flight-2day/6.jpg',
      '/images/marsa-alam/marsa-cairo-flight-2day/7.jpg',
      '/images/marsa-alam/marsa-cairo-flight-2day/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 92,
    highlights_de: [
  'Pyramiden von Gizeh',
  'Große Sphinx',
  'Grand Egyptian Museum (GEM)',
  'Memphis & Sakkara',
  'Zitadelle & Mohamed-Ali-Moschee',
  'Khan El Khalili',
  'Hotelübernachtung mit Pyramidenblick'
],
    highlights_en: [
  'Pyramids of Giza',
  'Great Sphinx',
  'Grand Egyptian Museum (GEM)',
  'Memphis & Saqqara',
  'Citadel & Mohamed Ali Mosque',
  'Khan El Khalili Bazaar',
  'Overnight stay with Pyramid view'
],
    itinerary_de: [
  'Tag 1: Flug nach Kairo, Memphis, Sakkara, Mittagessen, Pyramiden von Gizeh, Sphinx, Hotelübernachtung',
  'Tag 2: Grand Egyptian Museum (GEM), Zitadelle, Mohamed-Ali-Moschee, Khan El Khalili, El-Moez-Straße, Rückflug'
],
    itinerary_en: [
  'Day 1: Flight to Cairo, Memphis, Saqqara, Lunch, Pyramids of Giza, Great Sphinx, Overnight stay',
  'Day 2: Grand Egyptian Museum (GEM), Citadel, Mohamed Ali Mosque, Khan El Khalili, El-Moez Street, Flight back'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  '2 Inlandsflüge',
  'Pyramiden von Gizeh',
  'Große Sphinx',
  'Grand Egyptian Museum (GEM)',
  'Memphis',
  'Sakkara',
  'Zitadelle & Mohamed-Ali-Moschee',
  'Khan El Khalili',
  'Hotelübernachtung mit Frühstück',
  'Mittagessen an beiden Tagen',
  'Alle Transfers',
  'Eintrittsgebühren',
  'Softgetränke'
],
    included_en: [
  'English-speaking Egyptologist',
  '2 domestic flights',
  'Pyramids of Giza',
  'Great Sphinx',
  'Grand Egyptian Museum (GEM)',
  'Memphis',
  'Saqqara',
  'Citadel & Mohamed Ali Mosque',
  'Khan El Khalili Bazaar',
  'Hotel accommodation with breakfast',
  'Lunch on both days',
  'All transfers',
  'Entrance fees',
  'Soft drinks'
],
   excluded_de: [
  'Getränke im Restaurant',
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Drinks in restaurants',
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam, Transfer zum Flughafen Hurghada',

meeting_point_en: 'Hotel pickup in Marsa Alam, transfer to Hurghada Airport',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 540 },
      { persons: '3 Pers.', price: 490 },
      { persons: '4 Pers.', price: 490 },
      { persons: '5–6 Pers.', price: 455 },
      { persons: '7–8 Pers.', price: 455 },
    ],
    child_price: 275,
  },
  {
    id: 'marsa-luxor-1day',
    departure_city: 'marsa-alam',
    title_de: 'Tagesausflug nach Luxor',
    title_en: 'Luxor Day Tour',
    description_de: `
Erleben Sie mit Yallabina Tours einen unvergesslichen privaten Tagesausflug von Marsa Alam nach Luxor – der größten Freilichtstadt der Welt. Entdecken Sie die beeindruckendsten Monumente des Alten Ägyptens mit einem deutschsprachigen Ägyptologen an Ihrer Seite.

Besuchen Sie den gewaltigen Karnak-Tempel, das geheimnisvolle Tal der Könige mit seinen prachtvollen Königsgräbern, die monumentalen Memnonkolosse und den einzigartigen Terrassentempel der Königin Hatschepsut. Genießen Sie außerdem eine entspannte Motorbootfahrt auf dem Nil sowie ein leckeres Mittagessen in einem ausgewählten Restaurant.

Diese private Tour bietet maximalen Komfort, keine Verkaufsstopps und genügend Zeit, die berühmtesten Sehenswürdigkeiten Luxors in aller Ruhe zu entdecken.
`,
    description_en: `
Discover the wonders of ancient Egypt on a private full-day tour from Marsa Alam to Luxor with Yallabina Tours. Accompanied by a professional English-speaking Egyptologist, explore the world's greatest open-air museum at your own pace.

Visit the magnificent Karnak Temple, the legendary Valley of the Kings, the impressive Colossi of Memnon and the spectacular Temple of Queen Hatshepsut. Enjoy a relaxing motorboat ride on the Nile and a delicious lunch at a local restaurant.

Travel comfortably in a private air-conditioned vehicle with no shopping stops, allowing you to fully enjoy one of Egypt's most fascinating historical destinations.
`,
    price: 110,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/064a57a99ef384d44d10705e8b8ad6de.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-luxor-1day/1.jpg',
      '/images/marsa-alam/marsa-luxor-1day/2.jpg',
      '/images/marsa-alam/marsa-luxor-1day/3.jpg',
      '/images/marsa-alam/marsa-luxor-1day/4.jpg',
      '/images/marsa-alam/marsa-luxor-1day/5.jpg',
      '/images/marsa-alam/marsa-luxor-1day/6.jpg',
      '/images/marsa-alam/marsa-luxor-1day/7.jpg',
      '/images/marsa-alam/marsa-luxor-1day/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 245,
    highlights_de: [
  'Karnak-Tempel',
  'Tal der Könige',
  'Tempel der Hatschepsut',
  'Memnonkolosse',
  'Privater deutschsprachiger Ägyptologe'
],
    highlights_en: [
  'Karnak Temple',
  'Valley of the Kings',
  'Hatshepsut Temple',
  'Colossi of Memnon',
  'Private German-speaking Egyptologist'
],
   itinerary_de: [
  ' Hotelabholung in Marsa Alam',
  'Ankunft in Luxor & Karnak-Tempel',
  'Nilfahrt',
  'Mittagessen',
  'Tal der Könige',
  'Tempel der Hatschepsut',
  'Memnonkolosse',
  'Rückfahrt nach Marsa Alam'
],
    itinerary_en: [
  ' Hotel pickup in Marsa Alam',
  'Arrival in Luxor & Karnak Temple',
  'Nile boat ride',
  'Lunch',
  'Valley of the Kings',
  'Hatshepsut Temple',
  'Colossi of Memnon',
  'Return to Marsa Alam'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  'Karnak-Tempel',
  'Tal der Könige (3 Gräber)',
  'Tempel der Hatschepsut',
  'Memnonkolosse',
  ' Motorbootfahrt auf dem Nil',
  'Mittagessen',
  'Alle Transfers',
  'Eintrittsgebühren',
  'Softgetränke während der Fahrt'
],
    included_en: [
  'English-speaking Egyptologist',
  'Karnak Temple',
  'Valley of the Kings (3 tombs)',
  'Hatshepsut Temple',
  'Colossi of Memnon',
  'motorboat ride on the Nile',
  'Lunch',
  'All transfers',
  'Entrance fees',
  'Soft drinks during the drive'
],
    excluded_de: [
  'Getränke im Restaurant',
  'Grab von Tutanchamun',
  'Grab von Ramses VI.',
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Drinks in the restaurant',
  'Tutankhamun Tomb',
  'Tomb of Ramses VI',
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Ihr Hotel in Marsa Alam',
    meeting_point_en: 'Your hotel in Marsa Alam',
    languages_de: 'Deutsch, Englisch, ',
    languages_en: 'German, English, ',
    faq: [
      { question_de: 'Wie lange dauert die Fahrt von Marsa Alam nach Luxor?', question_en: 'How long is the drive from Marsa Alam to Luxor?', answer_de: 'Die Fahrt dauert etwa 4 Stunden. Wir machen unterwegs eine kurze Pause.', answer_en: 'The drive takes about 4 hours. We make a short break along the way.' },
    ],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 170 },
      { persons: '3 Pers.', price: 150 },
      { persons: '4 Pers.', price: 140 },
      { persons: '5–6 Pers.', price: 130 },
      { persons: '7–8 Pers.', price: 110 },
    ],
    child_price: 55,
  },
  {
    id: 'marsa-luxor-2day',
    departure_city: 'marsa-alam',
    title_de: '2-Tagesausflug nach Luxor',
    title_en: 'Luxor 2-Day Tour',
    description_de: `
Erleben Sie mit Yallabina Tours einen exklusiven privaten 2-Tages-Ausflug von Marsa Alam nach Luxor und entdecken Sie die bedeutendsten Sehenswürdigkeiten des alten Ägyptens in aller Ruhe. Begleitet von einem deutschsprachigen Ägyptologen erkunden Sie die beeindruckenden Tempel und Königsgräber ohne Zeitdruck.

Besuchen Sie den monumentalen Karnak-Tempel, den stimmungsvoll beleuchteten Luxor-Tempel bei Sonnenuntergang, das berühmte Tal der Könige, den Tempel der Königin Hatschepsut, den Medinet-Habu-Tempel sowie die gewaltigen Memnonkolosse. Freuen Sie sich außerdem auf eine entspannte Motorbootfahrt auf dem Nil, zwei Mittagessen sowie eine Übernachtung mit Halbpension in einem komfortablen Hotel in Luxor.

Diese private Tour bietet maximalen Komfort, persönliche Betreuung und keine Verkaufsstopps – perfekt für alle, die Luxor intensiv erleben möchten.
`,
    description_en: `
Discover the treasures of ancient Egypt on a private 2-day tour from Marsa Alam to Luxor with Yallabina Tours. Accompanied by an English-speaking Egyptologist, enjoy two unforgettable days exploring Luxor's most spectacular archaeological sites at a relaxed pace.

Visit the magnificent Karnak Temple, the beautifully illuminated Luxor Temple at sunset, the legendary Valley of the Kings, the Temple of Queen Hatshepsut, Medinet Habu Temple and the impressive Colossi of Memnon. Relax during a scenic motorboat ride on the Nile and enjoy two delicious lunches as well as an overnight stay with half board in a comfortable hotel in Luxor.

Travel comfortably in a private air-conditioned vehicle with no shopping stops and enjoy one of Egypt's most complete historical experiences.
`,
    price: 260,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/d15c688bb54fb51a9d428307d76ed881.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-luxor-2day/1.jpg',
      '/images/marsa-alam/marsa-luxor-2day/2.jpg',
      '/images/marsa-alam/marsa-luxor-2day/3.jpg',
      '/images/marsa-alam/marsa-luxor-2day/4.jpg',
      '/images/marsa-alam/marsa-luxor-2day/5.jpg',
      '/images/marsa-alam/marsa-luxor-2day/6.jpg',
      '/images/marsa-alam/marsa-luxor-2day/7.jpg',
      '/images/marsa-alam/marsa-luxor-2day/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 156,
    highlights_de: [
  'Karnak-Tempel',
  'Luxor-Tempel bei Sonnenuntergang',
  'Tal der Könige',
  'Tempel der Hatschepsut',
  'Medinet-Habu-Tempel',
  'Memnonkolosse',
  ' Nilfahrt',
  'Übernachtung in Luxor'
],
    highlights_en: [
  'Karnak Temple',
  'Luxor Temple at sunset',
  'Valley of the Kings',
  'Hatshepsut Temple',
  'Medinet Habu Temple',
  'Colossi of Memnon',
  'Nile boat ride',
  'Hotel overnight in Luxor'
],
    itinerary_de: [
  'Tag 1: Hotelabholung in Marsa Alam',
  'Karnak-Tempel',
  'Mittagessen',
  'Luxor-Tempel',
  'Übernachtung in Luxor',
  'Tag 2: Tal der Könige',
  'Tempel der Hatschepsut',
  'Medinet-Habu-Tempel',
  'Memnonkolosse',
  ' Nilfahrt',
  'Mittagessen',
  'Rückfahrt nach Marsa Alam'
],
    itinerary_en: [
  'Day 1: Hotel pickup in Marsa Alam',
  'Karnak Temple',
  'Lunch',
  'Luxor Temple',
  'Hotel overnight',
  'Day 2: Valley of the Kings',
  'Hatshepsut Temple',
  'Medinet Habu Temple',
  'Colossi of Memnon',
  ' Nile boat ride',
  'Lunch',
  'Return to Marsa Alam'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  'Karnak-Tempel',
  'Luxor-Tempel',
  'Tal der Könige (3 Gräber)',
  'Tempel der Hatschepsut',
  'Medinet-Habu-Tempel',
  'Memnonkolosse',
  ' Motorbootfahrt auf dem Nil',
  '1 Übernachtung mit Halbpension',
  'Mittagessen an beiden Tagen',
  'Alle Transfers',
  'Eintrittsgebühren',
  'Softgetränke während der Fahrt'
],
    included_en: [
  'English-speaking Egyptologist',
  'Karnak Temple',
  'Luxor Temple',
  'Valley of the Kings (3 tombs)',
  'Hatshepsut Temple',
  'Medinet Habu Temple',
  'Colossi of Memnon',
  ' motorboat ride on the Nile',
  '1 hotel overnight with half board',
  'Lunch on both days',
  'All transfers',
  'Entrance fees',
  'Soft drinks during the drive'
],
    excluded_de: [
  'Getränke im Restaurant',
  'Heißluftballonfahrt',
  'Ton- und Lichtshow',
  'Grab von Tutanchamun',
  'Grab von Ramses VI.',
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Drinks in the restaurant',
  'Hot air balloon ride',
  'Sound and Light Show',
  'Tutankhamun Tomb',
  'Tomb of Ramses VI',
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 300 },
      { persons: '3 Pers.', price: 290 },
      { persons: '4 Pers.', price: 280 },
      { persons: '5–6 Pers.', price: 270 },
      { persons: '7–8 Pers.', price: 260 },
    ],
    child_price: 160,
  },
  {
    id: 'marsa-aswan-1day',
    departure_city: 'marsa-alam',
    title_de: 'Privater Ausflug nach Assuan',
    title_en: 'Private Aswan Excursion',
    description_de: `
Erleben Sie mit Yallabina Tours einen privaten Tagesausflug von Marsa Alam nach Assuan und entdecken Sie eine der schönsten Städte Oberägyptens. Begleitet von einem deutschsprachigen Ägyptologen besuchen Sie die wichtigsten Sehenswürdigkeiten entlang des Nils.

Besichtigen Sie den wunderschönen Philae-Tempel, der nach dem Bau des Assuan-Staudamms auf eine Insel versetzt wurde, den berühmten unvollendeten Obelisken sowie den imposanten Assuan-Hochdamm. Genießen Sie außerdem ein leckeres Mittagessen in einem lokalen Restaurant und reisen Sie bequem in einem klimatisierten Fahrzeug.

Diese private Tour bietet ausreichend Zeit an allen Sehenswürdigkeiten, persönlichen Service und keine Verkaufsstopps – ideal für alle, die Assuan entspannt entdecken möchten.
`,
    description_en: `
Discover the highlights of Aswan on a private full-day tour from Marsa Alam with Yallabina Tours. Accompanied by an English-speaking Egyptologist, explore one of southern Egypt's most fascinating cities and its world-famous monuments.

Visit the beautiful Philae Temple, relocated to an island after the construction of the Aswan High Dam, see the famous Unfinished Obelisk and admire the impressive Aswan High Dam. Enjoy a delicious lunch at a local restaurant while travelling comfortably in a private air-conditioned vehicle.

With no shopping stops and plenty of time at each attraction, this tour is the perfect way to experience the history and beauty of Aswan.
`,
    price: 140,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/361d34f945937b1e1face5c6a7e79b44.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-aswan-1day/1.jpg',
      '/images/marsa-alam/marsa-aswan-1day/2.jpg',
      '/images/marsa-alam/marsa-aswan-1day/3.jpg',
      '/images/marsa-alam/marsa-aswan-1day/4.jpg',
      '/images/marsa-alam/marsa-aswan-1day/5.jpg',
      '/images/marsa-alam/marsa-aswan-1day/6.jpg',
      '/images/marsa-alam/marsa-aswan-1day/7.jpg',
      '/images/marsa-alam/marsa-aswan-1day/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 89,
    highlights_de: [
  'Philae-Tempel',
  'Unvollendeter Obelisk',
  'Assuan-Hochdamm',
  'Motorboot zur Philae-Insel',
  'Privater deutschsprachiger Ägyptologe',
  'Mittagessen'
],
    highlights_en: [
  'Philae Temple',
  'Unfinished Obelisk',
  'Aswan High Dam',
  'Motorboat to Philae Island',
  'English-speaking Egyptologist',
  'Lunch'
],
   itinerary_de: [
  ' Hotelabholung in Marsa Alam',
  'Ankunft in Assuan',
  'Motorboot zum Philae-Tempel',
  'Unvollendeter Obelisk',
  'Mittagessen',
  'Assuan-Hochdamm',
  'Rückfahrt nach Marsa Alam'
],
   itinerary_en: [
  ' Hotel pickup in Marsa Alam',
  'Arrival in Aswan',
  'Motorboat to Philae Temple',
  'Unfinished Obelisk',
  'Lunch',
  'Aswan High Dam',
  'Return to Marsa Alam'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  'Philae-Tempel',
  'Motorboot zur Philae-Insel',
  'Unvollendeter Obelisk',
  'Assuan-Hochdamm',
  'Mittagessen',
  'Alle Transfers',
  'Eintrittsgebühren',
  'Softgetränke während der Fahrt'
],
    included_en: [
  'English-speaking Egyptologist',
  'Philae Temple',
  'Motorboat to Philae Island',
  'Unfinished Obelisk',
  'Aswan High Dam',
  'Lunch',
  'All transfers',
  'Entrance fees',
  'Soft drinks during the drive'
],
   excluded_de: [
  'Getränke im Restaurant',
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Drinks in the restaurant',
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 175 },
      { persons: '3 Pers.', price: 165 },
      { persons: '4 Pers.', price: 155 },
      { persons: '5–6 Pers.', price: 145 },
      { persons: '7–8 Pers.', price: 140 },
    ],
    child_price: 60,
  },
  {
    id: 'marsa-aswan-abu-simbel-2day',
    departure_city: 'marsa-alam',
    title_de: '2-Tagesausflug Assuan & Abu Simbel',
    title_en: 'Aswan & Abu Simbel 2-Day Tour',
    description_de: `
Erleben Sie mit Yallabina Tours einen exklusiven privaten 2-Tages-Ausflug von Marsa Alam nach Assuan und Abu Simbel. Entdecken Sie die beeindruckendsten Sehenswürdigkeiten Oberägyptens und besuchen Sie die monumentalen Felsentempel von Ramses II., die zum UNESCO-Weltkulturerbe gehören.

Am ersten Tag besichtigen Sie den wunderschönen Philae-Tempel, den berühmten unvollendeten Obelisken sowie den imposanten Assuan-Hochdamm. Nach einer Übernachtung mit Halbpension in einem komfortablen 4-Sterne-Hotel fahren Sie am zweiten Tag nach Abu Simbel und erkunden die weltberühmten Tempel von Ramses II. und Königin Nefertari.

Freuen Sie sich auf eine komfortable private Reise mit einem deutschsprachigen Ägyptologen, zwei Mittagessen, keine Verkaufsstopps und ausreichend Zeit an allen Sehenswürdigkeiten.
`,
    description_en: `
Join Yallabina Tours on an unforgettable private 2-day journey from Marsa Alam to Aswan and Abu Simbel. Accompanied by an English-speaking Egyptologist, discover the most famous monuments of southern Egypt, including the spectacular rock temples of Ramses II., a UNESCO World Heritage Site.

On the first day, visit the beautiful Philae Temple, the famous Unfinished Obelisk and the impressive Aswan High Dam. After an overnight stay with half board in a comfortable 4-star hotel, continue to Abu Simbel on the second day to explore the magnificent temples of Ramses II and Queen Nefertari.

Travel comfortably in a private air-conditioned vehicle, enjoy two delicious lunches and experience Egypt without shopping stops or unnecessary delays.
`,
    price: 380,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/6cb2891a6d609153b5f345232054aa16.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/1.jpg',
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/2.jpg',
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/3.jpg',
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/4.jpg',
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/5.jpg',
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/6.jpg',
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/7.jpg',
      '/images/marsa-alam/marsa-aswan-abu-simbel-2day/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 74,
    highlights_de: [
  'Abu-Simbel-Tempel',
  'Philae-Tempel',
  'Unvollendeter Obelisk',
  'Assuan-Hochdamm',
  'Übernachtung im 4-Sterne-Hotel',
  'Deutschsprachiger Ägyptologe'
],
    highlights_en: [
  'Abu Simbel Temples',
  'Philae Temple',
  'Unfinished Obelisk',
  'Aswan High Dam',
  '4-star hotel overnight',
  'English-speaking Egyptologist'
],
    itinerary_de: [
  'Tag 1: Hotelabholung in Marsa Alam',
  'Philae-Tempel',
  'Unvollendeter Obelisk',
  'Mittagessen',
  'Assuan-Hochdamm',
  'Übernachtung in Assuan',
  'Tag 2: Abu-Simbel-Tempel',
  'Mittagessen',
  'Rückfahrt nach Marsa Alam'
],
    itinerary_en: [
  'Day 1: Hotel pickup in Marsa Alam',
  'Philae Temple',
  'Unfinished Obelisk',
  'Lunch',
  'Aswan High Dam',
  'Hotel overnight in Aswan',
  'Day 2: Abu Simbel Temples',
  'Lunch',
  'Return to Marsa Alam'
],
    included_de: [
  'Deutschsprachiger Ägyptologe',
  'Philae-Tempel',
  'Unvollendeter Obelisk',
  'Assuan-Hochdamm',
  'Abu-Simbel-Tempel',
  '1 Übernachtung mit Halbpension im 4-Sterne-Hotel',
  'Mittagessen an beiden Tagen',
  'Alle Transfers',
  'Eintrittsgebühren',
  'Softgetränke während der Fahrt'
],
    included_en: [
  'English-speaking Egyptologist',
  'Philae Temple',
  'Unfinished Obelisk',
  'Aswan High Dam',
  'Abu Simbel Temples',
  '1 overnight stay with half board in a 4-star hotel',
  'Lunch on both days',
  'All transfers',
  'Entrance fees',
  'Soft drinks during the drive'
],
   excluded_de: [
  'Getränke im Restaurant',
  'Ton- und Lichtshow im Philae-Tempel',
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Drinks in the restaurant',
  'Sound and Light Show at Philae Temple',
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 450 },
      { persons: '3 Pers.', price: 430 },
      { persons: '4 Pers.', price: 410 },
      { persons: '5–6 Pers.', price: 390 },
      { persons: '7–8 Pers.', price: 380 },
    ],
    child_price: 225,
  },
  {
    id: 'marsa-sataya-1day',
    departure_city: 'marsa-alam',
    title_de: 'Sataya Delfinriff Bootstour',
    title_en: 'Sataya Dolphin Reef Boat Tour',
    description_de: 'Erleben Sie einen unvergesslichen Tagesausflug zum berühmten Sataya Dolphin Reef ab Marsa Alam. Schwimmen Sie mit wilden Spinnerdelfinen in ihrer natürlichen Umgebung und entdecken Sie eines der schönsten unberührten Korallenriffe des Roten Meeres. Genießen Sie mehrere Schnorchelstopps, ein frisch zubereitetes Mittagessen an Bord sowie warme und kalte Getränke während des gesamten Tages. Ein perfekter Ausflug für Delfin- und Naturliebhaber.',
    description_en: 'Enjoy an unforgettable full-day boat trip to the famous Sataya Dolphin Reef from Marsa Alam. Swim with wild spinner dolphins in their natural habitat and discover one of the Red Sea’s most beautiful untouched coral reefs. Relax with several snorkeling stops, a freshly prepared lunch on board, and hot & cold drinks throughout the day. A perfect experience for dolphin and nature lovers.',
    price: 70,
    duration: '1 Tag',
    duration_type: 'full_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/673207e38b0af0c5144205239672d812.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-sataya-1day/1.jpg',
      '/images/marsa-alam/marsa-sataya-1day/2.jpg',
      '/images/marsa-alam/marsa-sataya-1day/3.jpg',
      '/images/marsa-alam/marsa-sataya-1day/4.jpg',
      '/images/marsa-alam/marsa-sataya-1day/5.jpg',
      '/images/marsa-alam/marsa-sataya-1day/6.jpg',
      '/images/marsa-alam/marsa-sataya-1day/7.jpg',
      '/images/marsa-alam/marsa-sataya-1day/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 312,
    highlights_de: [
  'Schwimmen mit wilden Spinnerdelfinen',
  'Sataya Dolphin Reef',
  '2–3 Schnorchelstopps',
  'Unberührte Korallenriffe',
  'Mittagessen an Bord',
  'Warme & kalte Getränke'
],
    highlights_en: [
  'Swim with wild spinner dolphins',
  'Sataya Dolphin Reef',
  '2–3 snorkeling stops',
  'Pristine coral reefs',
  'Lunch on board',
  'Hot & cold drinks'
],
  itinerary_de: [
  ' Hotelabholung in Marsa Alam',
  'Transfer zum Hafen',
  'Bootsfahrt zum Sataya Dolphin Reef',
  'Schwimmen mit Delfinen',
  '2–3 Schnorchelgänge',
  'Mittagessen an Bord',
  'Rückfahrt zum Hafen',
  'Transfer zum Hotel'
],
    itinerary_en: [
  'Hotel pickup in Marsa Alam',
  'Transfer to the marina',
  'Boat trip to Sataya Dolphin Reef',
  'Swim with dolphins',
  '2–3 snorkeling stops',
  'Lunch on board',
  'Return to the marina',
  'Transfer back to your hotel'
],
   included_de: [
  'Deutschsprachiger Reiseleiter',
  'Bootsausflug',
  '2–3 Schnorchelgänge',
  'Mittagessen an Bord',
  'Warme & kalte Getränke',
  'Toiletten an Bord',
  'Süßwasserduschen an Bord',
  'Alle Transfers',
  'Alle lokalen Steuern'
],
    included_en: [
  'English-speaking guide',
  'Boat trip',
  '2–3 snorkeling stops',
  'Lunch on board',
  'Hot & cold drinks',
  'On-board toilets',
  'Freshwater showers',
  'All transfers',
  'All local taxes'
],
   excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
meeting_point_en: 'Hotel pickup from your hotel in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 70 }],
    child_price: 40,
  },
  {
    id: 'marsa-sataya-2day',
    departure_city: 'marsa-alam',
    title_de: '2-Tage Sataya Bootstour',
    title_en: '2-Day Sataya Boat Tour',
    description_de: 'Erleben Sie ein unvergessliches 2-Tages-Abenteuer zum berühmten Sataya Dolphin Reef ab Marsa Alam. Verbringen Sie eine Nacht an Bord unter dem Sternenhimmel, schwimmen Sie mit wilden Spinnerdelfinen in ihrer natürlichen Umgebung und entdecken Sie spektakuläre Korallenriffe bei mehreren Schnorchelgängen. Genießen Sie Vollpension an Bord, traumhafte Sonnenaufgänge und zwei unvergessliche Tage auf dem Roten Meer.',
    description_en: 'Experience an unforgettable 2-day adventure to the famous Sataya Dolphin Reef from Marsa Alam. Spend the night on board under the stars, swim with wild spinner dolphins in their natural habitat, and explore spectacular coral reefs during multiple snorkeling stops. Enjoy full board, breathtaking sunrises, and two unforgettable days on the Red Sea.',
    price: 185,
    duration: '2 Tage',
    duration_type: 'multi_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/fb6030c0589518caad2034daff1a210c.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-sataya-2day/1.jpg',
      '/images/marsa-alam/marsa-sataya-2day/2.jpg',
      '/images/marsa-alam/marsa-sataya-2day/3.jpg',
      '/images/marsa-alam/marsa-sataya-2day/4.jpg',
      '/images/marsa-alam/marsa-sataya-2day/5.jpg',
      '/images/marsa-alam/marsa-sataya-2day/6.jpg',
      '/images/marsa-alam/marsa-sataya-2day/7.jpg',
      '/images/marsa-alam/marsa-sataya-2day/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 143,
    highlights_de: [
  'Schwimmen mit wilden Spinnerdelfinen',
  'Übernachtung an Bord',
  '5 Schnorchelgänge',
  'Sataya Dolphin Reef',
  'Sonnenaufgang auf dem Roten Meer',
  'Vollpension an Bord'
],
    highlights_en: [
  'Swim with wild spinner dolphins',
  'Overnight on board',
  '5 snorkeling stops',
  'Sataya Dolphin Reef',
  'Sunrise over the Red Sea',
  'Full board on board'
],
    itinerary_de: [
  'Tag 1: Hotelabholung in Marsa Alam',
  'Transfer zum Hafen',
  'Bootsfahrt zum Sataya Dolphin Reef',
  'Mehrere Schnorchelgänge & Delfinsuche',
  'Mittagessen',
  'Abendessen & Übernachtung an Bord',
  'Tag 2: Sonnenaufgang & Schnorcheln',
  'Frühstück',
  'Schnorcheln am Abu Galawa Riff',
  'Mittagessen',
  'Rückfahrt zum Hafen',
  'Transfer zum Hotel'
],
    itinerary_en: [
  'Day 1: Hotel pickup in Marsa Alam',
  'Transfer to the marina',
  'Boat trip to Sataya Dolphin Reef',
  'Multiple snorkeling stops & dolphin search',
  'Lunch',
  'Dinner & overnight on board',
  'Day 2: Sunrise & snorkeling',
  'Breakfast',
  'Snorkeling at Abu Galawa Reef',
  'Lunch',
  'Return to the marina',
  'Transfer back to your hotel'
],
    included_de: [
  'Deutschsprachiger Reiseleiter',
  'Übernachtung an Bord',
  '5 Schnorchelgänge',
  'Mittagessen',
  'Abendessen',
  'Frühstück',
  'Warme & kalte Getränke',
  'Toiletten an Bord',
  'Süßwasserduschen an Bord',
  'Alle Transfers',
  'Alle lokalen Steuern'
],
    included_en: [
  'English-speaking guide',
  'Overnight on board',
  '5 snorkeling stops',
  'Lunch',
  'Dinner',
  'Breakfast',
  'Hot & cold drinks',
  'On-board toilets',
  'Freshwater showers',
  'All transfers',
  'All local taxes'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
   meeting_point_de: 'Hotelabholung in Marsa Alam',
meeting_point_en: 'Hotel pickup from your hotel in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 185 }],
    child_price: 95,
  },
  {
    id: 'marsa-sharm-el-luli',
    departure_city: 'marsa-alam',
    title_de: 'Sharm El-Luli',
    title_en: 'Sharm El-Luli',
    description_de: 'Entdecken Sie das Naturparadies Sharm El Luli ab Marsa Alam. Genießen Sie einen traumhaften Tag an einem der schönsten weißen Sandstrände des Roten Meeres mit kristallklarem Wasser und einem faszinierenden Hausriff. Schnorcheln Sie direkt vom Strand aus zwischen bunten Korallen und tropischen Fischen und entspannen Sie bei einem köstlichen Mittagessen sowie warmen und kalten Getränken.',
    description_en: 'Discover the natural paradise of Sharm El Luli from Marsa Alam. Spend a relaxing day at one of the Red Sea’s most beautiful white sandy beaches with crystal-clear water and an amazing house reef. Snorkel directly from the shore among colorful corals and tropical fish while enjoying lunch and hot & cold drinks.',
    price: 80,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/fb04f78e9465458247d1d484bc2506a5.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-sharm-el-luli/1.jpg',
      '/images/marsa-alam/marsa-sharm-el-luli/2.jpg',
      '/images/marsa-alam/marsa-sharm-el-luli/3.jpg',
      '/images/marsa-alam/marsa-sharm-el-luli/4.jpg',
      '/images/marsa-alam/marsa-sharm-el-luli/5.jpg',
      '/images/marsa-alam/marsa-sharm-el-luli/6.jpg',
      '/images/marsa-alam/marsa-sharm-el-luli/7.jpg',
      '/images/marsa-alam/marsa-sharm-el-luli/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 215,
    highlights_de: [
  'Weißer Sandstrand',
  'Kristallklares Wasser',
  'Hausriff zum Schnorcheln',
  'Schnorcheln direkt vom Strand',
  'Mittagessen',
  'Warme & kalte Getränke'
],
   highlights_en: [
  'White sandy beach',
  'Crystal-clear water',
  'House reef for snorkeling',
  'Shore snorkeling',
  'Lunch',
  'Hot & cold drinks'
],
    itinerary_de: [
  'Hotelabholung in Marsa Alam',
  'Transfer nach Sharm El Luli',
  'Schnorcheln am Hausriff',
  'Freizeit am Strand',
  'Mittagessen',
  'Weitere Zeit zum Schwimmen & Schnorcheln',
  'Rückfahrt zum Hotel'
],
    itinerary_en: [
  'Hotel pickup in Marsa Alam',
  'Transfer to Sharm El Luli',
  'Snorkeling at the house reef',
  'Free time on the beach',
  'Lunch',
  'More time for swimming & snorkeling',
  'Transfer back to your hotel'
],
   included_de: [
  'Deutschsprachiger Reiseleiter',
  'Schnorchelausrüstung',
  'Mittagessen',
  'Warme & kalte Getränke',
  'Toiletten',
  'Süßwasserduschen',
  'Sonnenschirme',
  'Alle Transfers',
  'Alle lokalen Steuern'
],
    included_en: [
  'English-speaking guide',
  'Snorkeling equipment',
  'Lunch',
  'Hot & cold drinks',
  'Toilets',
  'Freshwater showers',
  'Sun umbrellas',
  'All transfers',
  'All local taxes'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 80 }],
    child_price: 40,
  },
  {
    id: 'marsa-abu-dabbab',
    departure_city: 'marsa-alam',
    title_de: 'Abu Dabbab',
    title_en: 'Abu Dabbab',
    description_de: 'Erleben Sie einen unvergesslichen Tagesausflug zur berühmten Abu Dabbab Bucht ab Marsa Alam. Schnorcheln Sie direkt vom Strand aus mit riesigen Meeresschildkröten und entdecken Sie mit etwas Glück die seltenen Dugongs (Seekühe). Genießen Sie kristallklares Wasser, farbenfrohe Korallenriffe, ein leckeres Mittagessen sowie erfrischende Kaltgetränke – ein perfekter Ausflug für die ganze Familie.',
    description_en: 'Enjoy an unforgettable day trip to the famous Abu Dabbab Bay from Marsa Alam. Snorkel directly from the beach with giant sea turtles and, with a little luck, spot the rare dugongs. Discover crystal-clear water, colorful coral reefs, a delicious lunch, and refreshing cold drinks – the perfect excursion for the whole family.',
    price: 75,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/d1f8f5de41e1e7053ac05fdcabc59c90.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-abu-dabbab/1.jpg',
      '/images/marsa-alam/marsa-abu-dabbab/2.jpg',
      '/images/marsa-alam/marsa-abu-dabbab/3.jpg',
      '/images/marsa-alam/marsa-abu-dabbab/4.jpg',
      '/images/marsa-alam/marsa-abu-dabbab/5.jpg',
      '/images/marsa-alam/marsa-abu-dabbab/6.jpg',
      '/images/marsa-alam/marsa-abu-dabbab/7.jpg',
      '/images/marsa-alam/marsa-abu-dabbab/8.jpg',
    ],
    rating: 4.9,
    reviews_count: 445,
    highlights_de: [
  'Riesige Meeresschildkröten',
  'Chance auf Dugongs (Seekühe)',
  'Schnorcheln direkt vom Strand',
  'Kristallklares Wasser',
  'Mittagessen',
  'Kalte Getränke'
],
    highlights_en: [
  'Giant sea turtles',
  'Chance to see dugongs',
  'Beach snorkeling',
  'Crystal-clear water',
  'Lunch',
  'Cold drinks'
],
    itinerary_de: [
  ' Hotelabholung in Marsa Alam',
  'Transfer zur Abu Dabbab Bucht',
  'Schnorcheln mit Meeresschildkröten',
  'Freizeit am Strand',
  'Mittagessen',
  'Weitere Zeit zum Schwimmen & Schnorcheln',
  'Rückfahrt zum Hotel'
],
    itinerary_en: [
  ' Hotel pickup in Marsa Alam',
  'Transfer to Abu Dabbab Bay',
  'Snorkeling with sea turtles',
  'Free time on the beach',
  'Lunch',
  'More time for swimming & snorkeling',
  'Transfer back to your hotel'
],
    included_de: [
  'Deutschsprachiger Reiseleiter',
  'Schnorchelausrüstung',
  'Mittagessen',
  'Kalte Getränke',
  'Toiletten',
  'Süßwasserduschen',
  'Sonnenschirme',
  'Alle Transfers',
  'Alle lokalen Steuern'
],
    included_en: [
  'English-speaking guide',
  'Snorkeling equipment',
  'Lunch',
  'Cold drinks',
  'Toilets',
  'Freshwater showers',
  'Sun umbrellas',
  'All transfers',
  'All local taxes'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
    excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 75 }],
    child_price: 45,
  },
  {
    id: 'marsa-mubarak-boat',
    departure_city: 'marsa-alam',
    title_de: 'Marsa Mubarak Bootstour',
    title_en: 'Marsa Mubarak Boat Tour',
    description_de: 'Erleben Sie einen unvergesslichen Bootsausflug zur berühmten Marsa Mubarak Bucht ab Marsa Alam. Entdecken Sie farbenfrohe Korallenriffe, schwimmen Sie mit Meeresschildkröten und begegnen Sie mit etwas Glück den seltenen Dugongs (Seekühen). Genießen Sie mehrere Schnorchelstopps, ein leckeres Mittagessen an Bord sowie warme und kalte Getränke während eines entspannten Tages auf dem Roten Meer.',
    description_en: 'Enjoy an unforgettable boat trip to the famous Marsa Mubarak Bay from Marsa Alam. Discover colorful coral reefs, swim with sea turtles, and with a little luck encounter the rare dugongs. Enjoy multiple snorkeling stops, a delicious lunch on board, and hot & cold drinks during a relaxing day on the Red Sea.',
    price: 60,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/fa8ca5a04f7ecb8f40ff14987c09cd34.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-mubarak-boat/1.jpg',
      '/images/marsa-alam/marsa-mubarak-boat/2.jpg',
      '/images/marsa-alam/marsa-mubarak-boat/3.jpg',
      '/images/marsa-alam/marsa-mubarak-boat/4.jpg',
      '/images/marsa-alam/marsa-mubarak-boat/5.jpg',
      '/images/marsa-alam/marsa-mubarak-boat/6.jpg',
      '/images/marsa-alam/marsa-mubarak-boat/7.jpg',
      '/images/marsa-alam/marsa-mubarak-boat/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 189,
   highlights_de: [
  '2–3 Schnorchelstopps',
  'Meeresschildkröten',
  'Chance auf Dugongs (Seekühe)',
  'Farbenfrohe Korallenriffe',
  'Mittagessen an Bord',
  'Warme & kalte Getränke'
],
    highlights_en: [
  '2–3 snorkeling stops',
  'Sea turtles',
  'Chance to see dugongs',
  'Colorful coral reefs',
  'Lunch on board',
  'Hot & cold drinks'
],
    itinerary_de: [
  '08:00 – Hotelabholung in Marsa Alam',
  'Transfer zum Hafen',
  'Bootsfahrt zur Marsa Mubarak Bucht',
  '2–3 Schnorchelgänge',
  'Mittagessen an Bord',
  'Rückfahrt zum Hafen',
  'Transfer zum Hotel'
],
    itinerary_en: [
  '08:00 – Hotel pickup in Marsa Alam',
  'Transfer to the marina',
  'Boat trip to Marsa Mubarak Bay',
  '2–3 snorkeling stops',
  'Lunch on board',
  'Return to the marina',
  'Transfer back to your hotel'
],
    included_de: [
  'Deutschsprachiger Reiseleiter',
  'Schnorchelausrüstung',
  '2–3 Schnorchelgänge',
  'Mittagessen an Bord',
  'Warme & kalte Getränke',
  'Toiletten an Bord',
  'Süßwasserduschen an Bord',
  'Alle Transfers',
  'Alle lokalen Steuern'
],
    included_en: [
  'English-speaking guide',
  'Snorkeling equipment',
  '2–3 snorkeling stops',
  'Lunch on board',
  'Hot & cold drinks',
  'On-board toilets',
  'Freshwater showers',
  'All transfers',
  'All local taxes'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
   excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
meeting_point_en: 'Hotel pickup from your hotel in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 60 }],
    child_price: 30,
  },
  {
    id: 'marsa-quad-3h',
    departure_city: 'marsa-alam',
    title_de: '3 Stunden Quad-Tour',
    title_en: '3 Hour Quad Tour',
    description_de: 'Erleben Sie eine aufregende Quad-Tour durch die Wüste von Marsa Alam. Fahren Sie selbst mit dem Quad über Sanddünen bis zu einem traditionellen Beduinendorf. Genießen Sie einen Beduinentee, lernen Sie die Kultur der Wüste kennen und erleben Sie ein unvergessliches Abenteuer voller Action.',
    description_en: 'Experience an exciting quad bike adventure through the desert of Marsa Alam. Ride your own quad across the sand dunes to a traditional Bedouin village. Enjoy Bedouin tea, discover the desert culture, and experience an unforgettable off-road adventure.',
    price: 60,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/8df21201180672cd86e0d830d1f508a4.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-quad-3h/1.jpg',
      '/images/marsa-alam/marsa-quad-3h/2.jpg',
      '/images/marsa-alam/marsa-quad-3h/3.jpg',
      '/images/marsa-alam/marsa-quad-3h/4.jpg',
      '/images/marsa-alam/marsa-quad-3h/5.jpg',
      '/images/marsa-alam/marsa-quad-3h/6.jpg',
      '/images/marsa-alam/marsa-quad-3h/7.jpg',
      '/images/marsa-alam/marsa-quad-3h/8.jpg',
    ],
    rating: 4.7,
    reviews_count: 234,
    highlights_de: [
  'Quad fahren durch die Wüste',
  'Besuch eines Beduinendorfs',
  'Kamelritt',
  'Beduinentee',
  'Eigenes Quad',
  'Abenteuer in den Sanddünen'
],
    highlights_en: [
  'Quad biking through the desert',
  'Visit a Bedouin village',
  'Camel ride',
  'Bedouin tea',
  'Own quad bike',
  'Sand dune adventure'
],
    itinerary_de: [
  'Hotelabholung in Marsa Alam',
  'Transfer zur Quad-Station',
  'Sicherheitseinweisung & Probefahrt',
  '22 km Quadfahrt zum Beduinendorf',
  'Beduinentee & Kamelritt',
  'Rückfahrt mit dem Quad',
  'Transfer zurück zum Hotel'
],
    itinerary_en: [
  'Hotel pickup in Marsa Alam',
  'Transfer to the quad station',
  'Safety briefing & test ride',
  '22 km quad ride to the Bedouin village',
  'Bedouin tea & camel ride',
  'Quad ride back',
  'Transfer back to your hotel'
],
    included_de: [
  'Hoteltransfers',
  'Deutschsprachiger Reiseleiter',
  'Quad (1 Person pro Quad)',
  'Probefahrt',
  'Beduinentee',
  'Mineralwasser',
  'Alle Steuern und Servicegebühren'
],
    included_en: [
  'Hotel transfers',
  'English-speaking guide',
  'Quad bike (one per person)',
  'Test ride',
  'Bedouin tea',
  'Mineral water',
  'All taxes and service charges'
],
    excluded_de: ['Verpflegung', 'Sonnenbrille/Tuch', 'Trinkgeld'],
    excluded_en: ['Meals', 'Sunglasses/scarf', 'Tips'],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'flat',
    pricing_tiers: [{ persons: 'pro Person', price: 60 }],
    child_price: 30,
  },
  {
    id: 'marsa-ghost-city-quad',
    departure_city: 'marsa-alam',
    title_de: 'Ghost City Quad Safari',
    title_en: 'Ghost City Quad Safari',
    description_de: 'Erleben Sie eine aufregende Quad-Safari von Marsa Alam zur berühmten Ghost City bei Safaga. Fahren Sie mit dem Quad durch die beeindruckende Wüstenlandschaft, entdecken Sie die verlassene Bergbausiedlung Umm El Huwaytat und genießen Sie einen spektakulären Sonnenuntergang zwischen Bergen und Sanddünen.',
    description_en: 'Experience an exciting quad safari from Marsa Alam to the famous Ghost City near Safaga. Ride your quad through breathtaking desert landscapes, explore the abandoned mining village of Umm El Huwaytat, and enjoy a spectacular sunset surrounded by mountains and sand dunes.',
    price: 80,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/7e1fff75b3848abb80899974d17c3d94.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-ghost-city-quad/1.jpg',
      '/images/marsa-alam/marsa-ghost-city-quad/2.jpg',
      '/images/marsa-alam/marsa-ghost-city-quad/3.jpg',
      '/images/marsa-alam/marsa-ghost-city-quad/4.jpg',
      '/images/marsa-alam/marsa-ghost-city-quad/5.jpg',
      '/images/marsa-alam/marsa-ghost-city-quad/6.jpg',
      '/images/marsa-alam/marsa-ghost-city-quad/7.jpg',
      '/images/marsa-alam/marsa-ghost-city-quad/8.jpg',
    ],
    rating: 4.6,
    reviews_count: 112,
    highlights_de: [
  'Quad-Safari zur Ghost City bei Safaga',
  'Besichtigung der verlassenen Bergbausiedlung Umm El Huwaytat',
  'Fahrt durch die Wüste',
  'Spektakulärer Sonnenuntergang',
  'Fotostopps',
  'Abenteuer mit dem Quad'
],
    highlights_en: [
  'Quad safari to Ghost City near Safaga',
  'Explore the abandoned mining village of Umm El Huwaytat',
  'Ride through the desert',
  'Spectacular sunset',
  'Photo stops',
  'Quad adventure'
],
    itinerary_de: [
  'Hotelabholung in Marsa Alam',
  'Transfer zur Quad-Station',
  'Sicherheitseinweisung und Probefahrt',
  'Quadfahrt durch die Wüste',
  'Besichtigung der Ghost City (Umm El Huwaytat)',
  'Fotostopps',
  'Sonnenuntergang in der Wüste',
  'Rückfahrt zur Quad-Station',
  'Transfer zurück zum Hotel'
],
   itinerary_en: [
  'Hotel pickup in Marsa Alam',
  'Transfer to the quad station',
  'Safety briefing and test ride',
  'Quad ride through the desert',
  'Visit Ghost City (Umm El Huwaytat)',
  'Photo stops',
  'Sunset in the desert',
  'Return ride to the quad station',
  'Transfer back to your hotel'
],
    included_de: [
  'Hoteltransfers',
  'Deutschsprachiger Reiseleiter',
  'Quad (ein Quad pro Person)',
  'Sicherheitseinweisung',
  'Probefahrt',
  'Mineralwasser',
  'Alle Steuern und Servicegebühren'
],
    included_en: [
  'Hotel transfers',
  'English-speaking guide',
  'Quad bike (one quad per person)',
  'Safety briefing',
  'Test ride',
  'Mineral water',
  'All taxes and service charges'
],
    excluded_de: [
  'Persönliche Ausgaben',
  'Trinkgelder'
],
   excluded_en: [
  'Personal expenses',
  'Tips'
],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 90 },
      { persons: '3–4 Pers.', price: 85 },
      { persons: '5–8 Pers.', price: 80 },
    ],
  },
  {
    id: 'marsa-buggy',
    departure_city: 'marsa-alam',
    title_de: 'Buggy Safari',
    title_en: 'Buggy Safari',
    description_de: 'Mit dem Buggy durch die Wüste fahren und die Landschaft entdecken. Ein aufregendes Offroad-Abenteuer auf einem leistungsstarken Buggy durch die spektakuläre Wüstenlandschaft bei Marsa Alam.',
    description_en: 'Drive through the desert on a buggy and discover the landscape. An exciting off-road adventure on a powerful buggy through the spectacular desert landscape near Marsa Alam.',
    price: 65,
    duration: 'Halbtag',
    duration_type: 'half_day',
    image_url: 'https://static.readdy.ai/image/0828cfb8fca07f758a8edd19b751e27e/961e68ab63536b8607270c5d4f35e7ee.jpeg',
    gallery: [
      '/images/marsa-alam/marsa-buggy/1.jpg',
      '/images/marsa-alam/marsa-buggy/2.jpg',
      '/images/marsa-alam/marsa-buggy/3.jpg',
      '/images/marsa-alam/marsa-buggy/4.jpg',
      '/images/marsa-alam/marsa-buggy/5.jpg',
      '/images/marsa-alam/marsa-buggy/6.jpg',
      '/images/marsa-alam/marsa-buggy/7.jpg',
      '/images/marsa-alam/marsa-buggy/8.jpg',
    ],
    rating: 4.8,
    reviews_count: 89,
    highlights_de: ['Buggy Offroad-Abenteuer', 'Wüstendünen', 'Action & Adrenalin', 'Professioneller Guide', 'Atemberaubende Landschaft'],
    highlights_en: ['Buggy off-road adventure', 'Desert dunes', 'Action & adrenaline', 'Professional guide', 'Breathtaking landscape'],
    itinerary_de: ['09:00 — Abholung vom Hotel', '09:30 — Buggy-Einweisung', 'Wüstenfahrt durch Dünen', 'Fotostopp', '12:30 — Rückfahrt zum Hotel'],
    itinerary_en: ['09:00 — Hotel pickup', '09:30 — Buggy briefing', 'Desert ride through dunes', 'Photo stop', '12:30 — Return to hotel'],
    included_de: ['Buggy', 'Guide', 'Helm'],
    included_en: ['Buggy', 'Guide', 'Helmet'],
    excluded_de: ['Transfer (optional)', 'Verpflegung', 'Trinkgeld'],
    excluded_en: ['Transfer (optional)', 'Meals', 'Tips'],
    meeting_point_de: 'Hotelabholung in Marsa Alam',
    meeting_point_en: 'Hotel pickup in Marsa Alam',
    languages_de: 'Deutsch, Englisch',
    languages_en: 'German, English',
    faq: [],
    pricing_model: 'tiered',
    pricing_tiers: [
      { persons: '2 Pers.', price: 90 },
      { persons: '3 Pers.', price: 85 },
      { persons: '4 Pers.', price: 65 },
    ],
  },
];

export const toursFromHurghada = tours.filter((t) => t.departure_city === 'hurghada');
export const toursFromMarsaAlam = tours.filter((t) => t.departure_city === 'marsa-alam');